import { leadTimeCompositionSnapshotSchema } from "@erp/shared-types";
import { oc } from "@orpc/contract";
import { JSON_SCHEMA_OUTPUT_REGISTRY } from "@orpc/zod/zod4";
import { getLineStockPositionContract } from "./line-stock-position.contract";
import { getLineSupplyDemandContract } from "./line-supply-demand.contract";
import { ReleaseFreightChoiceSchema } from "./freight-options.contract";
import { DraftSubmissionChoiceSchema } from "./purchase-order-submission.contract";
import { z } from "zod";
import {
  LegCostSourceSchema,
  LegPriceBreakSchema,
  LineBuyTermsSchema,
  CostConstraintAdjustmentSchema,
  OPTION_LEG_TYPES,
  OPTION_PLAN_KINDS,
  OptionForwardBuySchema,
  TroubleDataSchema,
} from "@erp/shared-types";
import {
  hasAtMostOneExplicitBuyCart,
  SINGLE_BUY_CART_ERROR_MESSAGE,
} from "./replenishment-plan-validation";
import {
  GetDemandFilterOptionsInputSchema,
  GetDemandFilterOptionsResponseSchema,
} from "./forecast-runs.contract";

// ─── Shared schemas ─────────────────────────────────────────────────────────

export const OptionActionSchema = z.enum([
  "transfer",
  "buy",
  "buy_and_transfer",
  "stock",
]);
export const OverrideActionSchema = z.enum([
  "transfer",
  "buy",
  "buy_and_transfer",
]);
export const OptionSourceSchema = z.enum(["engine", "user"]);
export const OptionLegTypeSchema = z.enum(OPTION_LEG_TYPES);
export const FulfillmentSchema = z.enum(["cut", "ship_whole"]);
export const ComposePlanLegTypeSchema = z.enum(["buy", "transfer"]);
// Warehouse type of a transfer's source, so the UI can label a transfer as
// coming from the destination's hub vs. a peer (sibling) branch. `branch` =
// sibling-branch transfer; `hub` = hub transfer.
export const TransferSourceWarehouseTypeSchema = z
  .enum(["hub", "branch"])
  .nullable();
export const OptionLegStatusSchema = z.enum([
  "planned",
  "created",
  "cancelled",
]);

/**
 * The supplier cost agreement that priced a buy leg, read live from
 * `ap_supplier_cost_agreements` so the detail modal can say what the contract
 * offers: its line and description, where it applies, its window, the MOQ and
 * multiple it imposes and its tiers. The price the leg bought at stays the one
 * frozen in `priceBreak`; a tier here can differ when the agreement changed
 * after the run.
 */
export const SuggestionCostAgreementSchema = z.object({
  costAgreementId: z.string().uuid(),
  contractNumber: z.number().int(),
  contractLine: z.number().int(),
  description: z.string().nullable(),
  scopeKind: z.enum(["warehouse", "district", "division"]),
  // The warehouse a warehouse-scoped agreement names; null for the others.
  warehouseName: z.string().nullable(),
  divisionCode: z.string(),
  effectiveStart: z.iso.date(),
  effectiveEnd: z.iso.date(),
  minQuantity: z.number().nullable(),
  incrementalQuantity: z.number().nullable(),
  tiers: z.array(
    z.object({
      tier: z.number().int(),
      minQuantity: z.number(),
      unitCost: z.number(),
    }),
  ),
});
export type SuggestionCostAgreement = z.infer<
  typeof SuggestionCostAgreementSchema
>;

/** One vendor quantity price break loaded for a SKU at a ship-to (GEN-2955). */
export const VendorPriceBreakTierSchema = z.object({
  tier: z.number().int().nullable(),
  minQuantity: z.number(),
  unitCost: z.number(),
});
export type VendorPriceBreakTier = z.infer<typeof VendorPriceBreakTierSchema>;

/** One active contract price the buyer may select on a line (GEN-2956). */
export const BuyContractPriceOptionSchema = z.object({
  costAgreementId: z.string().uuid(),
  contractNumber: z.number().int(),
  unitCost: z.number(),
});
export type BuyContractPriceOption = z.infer<
  typeof BuyContractPriceOptionSchema
>;

// One leg of a composed option plan (a buy → PO line, or a transfer → TO line).
export const SuggestionOptionLegSchema = z.object({
  leadTimeComposition: leadTimeCompositionSnapshotSchema.nullable().optional(),
  leadTimeSettingsChangedSinceRun: z.boolean().optional(),
  legId: z.string().uuid(),
  legType: OptionLegTypeSchema,
  rank: z.number(),
  status: OptionLegStatusSchema,
  quantity: z.number().nullable(),
  unitCost: z.number().nullable(),
  estimatedCost: z.number().nullable(),
  freightEstimate: z.number().nullable(),
  estimatedLeadTimeDays: z.number().nullable(),
  sourceWarehouseId: z.string().uuid().nullable(),
  sourceWarehouseName: z.string().nullable(),
  sourceWarehouseType: TransferSourceWarehouseTypeSchema,
  shipToWarehouseId: z.string().uuid().nullable(),
  shipToWarehouseName: z.string().nullable(),
  supplierId: z.string().uuid().nullable(),
  supplierName: z.string().nullable(),
  vendorMoqQuantity: z.number().nullable(),
  vendorMovAmount: z.number().nullable(),
  vendorPrepaidFreightThreshold: z.number().nullable(),
  belowMinimum: z.boolean(),
  minimumGapAmount: z.number().nullable(),
  // All-units price-break provenance frozen when the engine priced this leg:
  // the tier it reached and the nearest boundary above it. Null on transfer /
  // stock legs, on buyer-composed plans, and on any leg written before the
  // engine started recording it.
  priceBreak: LegPriceBreakSchema.nullable(),
  // The agreement `priceBreak.costAgreementId` names. Null when no agreement
  // priced the leg, or the agreement row no longer exists.
  costAgreement: SuggestionCostAgreementSchema.nullable(),
  unitNumber: z.string().nullable(),
  unitQuantity: z.number().nullable(),
  fulfillment: FulfillmentSchema.nullable(),
});
export type SuggestionOptionLeg = z.infer<typeof SuggestionOptionLegSchema>;
// ─── Forward buying ─────────────────────────────────────────────────────────

/**
 * One forward-buy uplift as the board reads it: the provenance the engine froze
 * into the option's `metadata` (docs/procurement/forward-buying.md, decision
 * 12) plus the rationale sentence the option carries, so the coverage chip and
 * its tooltip need nothing else from the row.
 *
 * Null on every option that is not an uplift, which includes every option
 * written before the forward-buy pass existed.
 */
export const OptionForwardBuyDetailSchema = OptionForwardBuySchema.extend({
  rationale: z.string().nullable(),
});

export type OptionForwardBuyDetail = z.infer<
  typeof OptionForwardBuyDetailSchema
>;

/**
 * What forward buying currently adds to one vendor cart, computed live from the
 * cart's committed lines whose effective option is an uplift, on the same
 * figures `cartTotal` counts. Nothing about it is stored, so it can never
 * disagree with the quantities on the board.
 *
 * The remaining gap is deliberately absent: the board derives it from the
 * summary's own `cartTotal`, `movThreshold` and `freightThreshold`.
 */
export const CartForwardBuySummarySchema = z.object({
  /** Committed lines whose effective option is an uplift. */
  linesUplifted: z.number(),
  /** Units the uplifts add over their base quantities. */
  unitsAdded: z.number(),
  /**
   * Net spend change: each uplift as the cart total counts it, minus its base
   * quantity at the frozen baseline unit cost. Negative when an all-units price
   * break made the larger order cheaper.
   */
  spendDelta: z.number(),
});

export type CartForwardBuySummary = z.infer<typeof CartForwardBuySummarySchema>;

// A buyer-board line's approval state. Only `approved` and `auto_approved`
// lines go on an order. `trouble` lines never do, even when released;
// `rejected` is a trashed line. `auto_approved` is set by the engine under the
// org's auto-approve rules (off by default). `released` is a line a buyer
// already released, kept on the board until the next refresh (GEN-3119).
export const LineApprovalStateSchema = z.enum([
  "unapproved",
  "approved",
  "auto_approved",
  "trouble",
  "rejected",
  "released",
]);

export type LineApprovalState = z.infer<typeof LineApprovalStateSchema>;

// The state set grows (GEN-3119 added `released`), and it only appears in
// responses. x-extensible-enum tells oasdiff the set is allowed to grow.
JSON_SCHEMA_OUTPUT_REGISTRY.add(LineApprovalStateSchema, {
  enum: undefined,
  "x-extensible-enum": LineApprovalStateSchema.options,
} as { enum: undefined });

// Why a line is in Trouble: the customer is on credit hold (read live), or a
// buyer put the line on hold by hand.
export const LineTroubleReasonSchema = z.enum(["credit_hold", "manual"]);

export type LineTroubleReason = z.infer<typeof LineTroubleReasonSchema>;

export const SuggestionStatusSchema = z.enum([
  "pending",
  "accepted",
  "rejected",
  "ordered",
]);
// `manual`: a stock line the buyer added for a need's overflow (GEN-2938).
export const SuggestionTriggerSchema = z.enum([
  "deficit",
  "sales_order",
  "manual",
]);

// The trigger set grows (GEN-2938 added `manual`), and it only appears in
// responses. x-extensible-enum tells oasdiff the set is allowed to grow.
JSON_SCHEMA_OUTPUT_REGISTRY.add(SuggestionTriggerSchema, {
  enum: undefined,
  "x-extensible-enum": SuggestionTriggerSchema.options,
} as { enum: undefined });

export const SuggestionPrioritySchema = z.enum([
  "critical",
  "urgent",
  "standard",
  "fill",
]);

// ─── Stage 1: Supplier summaries ────────────────────────────────────────────

export const SupplierSummaryRowSchema = z.object({
  supplierId: z.string().uuid(),
  supplierName: z.string(),
  openSuggestionCount: z.number(),
  salesOrderSuggestionCount: z.number(),
  totalNeededQuantity: z.number(),
  totalEstimatedCost: z.number(),
  movThreshold: z.number().nullable(),
  cartTotal: z.number(),
  belowMinimumCount: z.number(),
});

export type SupplierSummaryRow = z.infer<typeof SupplierSummaryRowSchema>;

export const ListSupplierSummariesInputSchema = z.object({
  status: SuggestionStatusSchema.optional(),
  // Legacy single-division filter. Prefer `divisionIds` for new callers; both
  // are accepted and merged server-side (single → array of 1).
  divisionId: z.string().uuid().optional(),
  divisionIds: z.array(z.string().uuid()).optional(),
  // Destination-warehouse filter, scoped to the selected divisions. Empty/omitted
  // means "all warehouses in scope". Filters on s.destination_warehouse_id.
  warehouseIds: z.array(z.string().uuid()).optional(),
  // Board-wide "Show trashed": include rejected suggestions alongside pending.
  includeTrashed: z.boolean().optional(),
  priority: SuggestionPrioritySchema.optional(),
  search: z.string().optional(),
  batchId: z.string().uuid().optional(),
  page: z.number().int().min(1).optional().default(1),
  pageSize: z.number().int().min(1).max(500).optional().default(50),
});

export const ListSupplierSummariesResponseSchema = z.object({
  rows: z.array(SupplierSummaryRowSchema),
  totalRows: z.number(),
});

export type ListSupplierSummariesInput = z.infer<
  typeof ListSupplierSummariesInputSchema
>;
export type ListSupplierSummariesResponse = z.infer<
  typeof ListSupplierSummariesResponseSchema
>;

// ─── Stage 2: Supplier detail (suggestions for one supplier) ────────────────

export const SupplierSuggestionRowSchema = z.object({
  suggestionId: z.string().uuid(),
  productSku: z.string().nullable(),
  productName: z.string(),
  destinationWarehouseId: z.string().uuid(),
  destinationWarehouseName: z.string(),
  trigger: SuggestionTriggerSchema,
  priority: SuggestionPrioritySchema,
  status: SuggestionStatusSchema,
  neededQuantity: z.number(),
  currentOnHand: z.number(),
  currentNetPosition: z.number(),
  policyMin: z.number().nullable(),
  policyMax: z.number().nullable(),
  popularityCode: z.string().nullable(),
  topOption: z.object({
    optionId: z.string().uuid(),
    action: OptionActionSchema,
    buyQuantity: z.number().nullable(),
    unitCost: z.number().nullable(),
    totalCost: z.number().nullable(),
    rationale: z.string().nullable(),
    belowMinimum: z.boolean(),
    minimumGapAmount: z.number().nullable(),
  }),
});

export type SupplierSuggestionRow = z.infer<typeof SupplierSuggestionRowSchema>;

export const GetSupplierDetailInputSchema = z.object({
  supplierId: z.string().uuid(),
  status: SuggestionStatusSchema.optional(),
  search: z.string().optional(),
  page: z.number().int().min(1).optional().default(1),
  pageSize: z.number().int().min(1).max(500).optional().default(50),
});

export const SupplierConstraintSchema = z.object({
  warehouseId: z.string().uuid(),
  warehouseName: z.string(),
  productGroup: z.string().nullable(),
  constraintValue: z.number().nullable(),
  constraintUom: z.string().nullable(),
  isPreferred: z.boolean(),
});

export type SupplierConstraint = z.infer<typeof SupplierConstraintSchema>;

export const GetSupplierDetailResponseSchema = z.object({
  supplierId: z.string().uuid(),
  supplierName: z.string(),
  movThreshold: z.number().nullable(),
  cartTotal: z.number(),
  constraints: z.array(SupplierConstraintSchema),
  rows: z.array(SupplierSuggestionRowSchema),
  totalRows: z.number(),
});

export type GetSupplierDetailInput = z.infer<
  typeof GetSupplierDetailInputSchema
>;
export type GetSupplierDetailResponse = z.infer<
  typeof GetSupplierDetailResponseSchema
>;

// ─── Stage 3: Suggestion detail (all options for one suggestion) ────────────

export const SuggestionOptionSchema = z.object({
  optionId: z.string().uuid(),
  source: OptionSourceSchema,
  action: OptionActionSchema,
  rank: z.number(),
  supplierId: z.string().uuid().nullable(),
  supplierName: z.string().nullable(),
  shipToWarehouseId: z.string().uuid().nullable(),
  shipToWarehouseName: z.string().nullable(),
  buyQuantity: z.number().nullable(),
  unitCost: z.number().nullable(),
  totalCost: z.number().nullable(),
  estimatedFreightCost: z.number().nullable(),
  vendorMoqQuantity: z.number().nullable(),
  vendorIncrementQuantity: z.number().nullable(),
  vendorMovAmount: z.number().nullable(),
  vendorPrepaidFreightThreshold: z.number().nullable(),
  belowMinimum: z.boolean(),
  minimumGapAmount: z.number().nullable(),
  sourceWarehouseId: z.string().uuid().nullable(),
  sourceWarehouseName: z.string().nullable(),
  sourceWarehouseType: TransferSourceWarehouseTypeSchema,
  transferQuantity: z.number().nullable(),
  transferFreightEstimate: z.number().nullable(),
  rationale: z.string().nullable(),
  // Forward-buy provenance when this option is a phase 4c uplift; null on
  // every other option, engine- or buyer-authored.
  forwardBuy: OptionForwardBuyDetailSchema.nullable(),
  estimatedLeadTimeDays: z.number().nullable(),
  createdByUserId: z.string().uuid().nullable(),
  createdByUserName: z.string().nullable(),
  createdAt: z.string(),
  // The composed plan's legs. Flat fields above are derived from the
  // representative buy/transfer leg for the existing card; `legs` is the full
  // breakdown (supports multi-leg plans, e.g. two transfers).
  legs: z.array(SuggestionOptionLegSchema),
});

export type SuggestionOption = z.infer<typeof SuggestionOptionSchema>;

export const GetSuggestionDetailInputSchema = z.object({
  suggestionId: z.string().uuid(),
});

/**
 * The vendor cart the suggestion's effective buy ships in, as it stands now:
 * computed like the belt card's cart summary (every pending line anchored to
 * that supplier with that ship-to, committed lines at their effective cost),
 * with the vendor's thresholds for the ship-to. The options' own
 * `belowMinimum` / `minimumGapAmount` are frozen at the engine run and go
 * stale after a forward buy, a removal or a buyer edit; this does not.
 */
export const SuggestionCartStatusSchema = z.object({
  shipToWarehouseId: z.string().uuid(),
  shipToWarehouseName: z.string(),
  cartTotal: z.number(),
  movThreshold: z.number().nullable(),
  freightThreshold: z.number().nullable(),
});

export type SuggestionCartStatus = z.infer<typeof SuggestionCartStatusSchema>;

// How a replenishment run ended. Null on runs recorded before the outcome was
// tracked.
export const ReplenishmentRunStatusSchema = z
  .enum(["running", "completed", "failed"])
  .nullable();

export const BoardFreshnessRunSchema = z.object({
  batchId: z.string().uuid(),
  status: ReplenishmentRunStatusSchema,
  // ISO timestamps. `completedAt` is when the run ended, either way.
  startedAt: z.string(),
  completedAt: z.string().nullable(),
});

export const GetSuggestionDetailResponseSchema = z.object({
  suggestionId: z.string().uuid(),
  destinationWarehouseId: z.string().uuid(),
  destinationWarehouseName: z.string(),
  // ProductV3's key is the v3-only suggestion's SKU. This is null only on
  // legacy suggestions that have not been resolved to a ProductV3 row.
  productV3Key: z.string().nullable(),
  // Variant of the destination policy this suggestion is for. Present for
  // deficit (min/max) suggestions — needed to address the inventory-policy
  // override; null for some SO-driven lines.
  productVariantId: z.string().uuid().nullable(),
  productSku: z.string().nullable(),
  productName: z.string(),
  trigger: SuggestionTriggerSchema,
  priority: SuggestionPrioritySchema,
  status: SuggestionStatusSchema,
  neededQuantity: z.number(),
  currentOnHand: z.number(),
  currentNetPosition: z.number(),
  policyMin: z.number().nullable(),
  policyMax: z.number().nullable(),
  // Forecasting-engine outputs on the destination policy (null until the engine
  // has run for this SKU×warehouse). safetyStock / reorderPoint are the
  // demand-driven buffer + reorder level; orderUpToLevel is the target the
  // deficit fills to (what the quantity reconciles against: needed = orderUpTo −
  // net); targetAvailability is the service level; forecastCalculatedAt = when
  // they were computed.
  safetyStock: z.number().nullable(),
  reorderPoint: z.number().nullable(),
  orderUpToLevel: z.number().nullable(),
  targetAvailability: z.number().nullable(),
  forecastCalculatedAt: z.string().nullable(),
  // Buyer's per-item source decision (INITINFC giparent1) on the destination
  // policy: 'transfer' (from buyerSourceWarehouse), 'buy', or null (unset).
  // buyerSourceHonored = the recommended/effective option actually satisfies it
  // (false = the engine fell back, e.g. the pinned source was out of stock).
  buyerSourceType: z.enum(["transfer", "buy"]).nullable(),
  buyerSourceWarehouseId: z.string().uuid().nullable(),
  buyerSourceWarehouseName: z.string().nullable(),
  buyerSourceHonored: z.boolean(),
  chosenOptionId: z.string().uuid().nullable(),
  reviewedByUserName: z.string().nullable(),
  reviewedAt: z.string().nullable(),
  batchId: z.string().uuid(),
  generatedAt: z.string(),
  // The engine run that last touched the line (its batch), with its outcome
  // and end time, for the Line footer's refresh stamp (GEN-3285). Null when
  // the batch row is gone.
  engineRun: BoardFreshnessRunSchema.nullable().default(null),
  // Days the line has sat pending since the engine first raised it, the
  // buying-orders list's rule (GEN-2940); null once it is no longer pending.
  daysOnOrder: z.number().int().nullable().default(null),
  // Triggering sales-order context — null for deficit-triggered suggestions.
  // Exposed here so the slidein can embed the SO detail inline instead of
  // opening a separate SalesOrderDetailModal.
  triggerSalesOrderId: z.string().uuid().nullable(),
  triggerSalesOrderNumber: z.string().nullable(),
  triggerSalesOrderLineItemId: z.string().uuid().nullable(),
  lotLevelDeficit: z.boolean(),
  // Lead-time override active RIGHT NOW for this product x destination
  // warehouse, resolved live rather than read off the suggestion. A pin set
  // after the engine run does not change the persisted
  // `estimatedLeadTimeDays`, so the UI shows this pinned value with the saved
  // estimate struck through instead of silently contradicting the override
  // dialog. Null when no override applies.
  leadTimeOverrideDays: z.number().nullable(),
  // The live cart of the effective option's buy leg. Null when the effective
  // option buys nothing, or the suggestion is no longer pending.
  cart: SuggestionCartStatusSchema.nullable(),
  options: z.array(SuggestionOptionSchema),
});

export type GetSuggestionDetailInput = z.infer<
  typeof GetSuggestionDetailInputSchema
>;
export type GetSuggestionDetailResponse = z.infer<
  typeof GetSuggestionDetailResponseSchema
>;

// ─── Transfer summaries (suggestions with transfer-only top option) ─────────

export const TransferSummaryRowSchema = z.object({
  suggestionId: z.string().uuid(),
  productSku: z.string().nullable(),
  productName: z.string(),
  destinationWarehouseId: z.string().uuid(),
  destinationWarehouseName: z.string(),
  sourceWarehouseId: z.string().uuid(),
  sourceWarehouseName: z.string(),
  priority: SuggestionPrioritySchema,
  status: SuggestionStatusSchema,
  neededQuantity: z.number(),
  transferQuantity: z.number(),
  transferFreightEstimate: z.number().nullable(),
  rationale: z.string().nullable(),
});

export type TransferSummaryRow = z.infer<typeof TransferSummaryRowSchema>;

export const ListTransferSummariesInputSchema = z.object({
  status: SuggestionStatusSchema.optional(),
  divisionId: z.string().uuid().optional(),
  divisionIds: z.array(z.string().uuid()).optional(),
  // Destination-warehouse filter (see ListSupplierSummariesInputSchema).
  warehouseIds: z.array(z.string().uuid()).optional(),
  search: z.string().optional(),
  page: z.number().int().min(1).optional().default(1),
  pageSize: z.number().int().min(1).max(500).optional().default(50),
});

export const ListTransferSummariesResponseSchema = z.object({
  rows: z.array(TransferSummaryRowSchema),
  totalRows: z.number(),
});

export type ListTransferSummariesInput = z.infer<
  typeof ListTransferSummariesInputSchema
>;
export type ListTransferSummariesResponse = z.infer<
  typeof ListTransferSummariesResponseSchema
>;

// ─── No-source suggestions (no fulfillment option found) ────────────────────

export const NoSourceRowSchema = z.object({
  suggestionId: z.string().uuid(),
  productSku: z.string().nullable(),
  productName: z.string(),
  destinationWarehouseId: z.string().uuid(),
  destinationWarehouseName: z.string(),
  trigger: SuggestionTriggerSchema,
  triggerSalesOrderId: z.string().uuid().nullable(),
  triggerSalesOrderNumber: z.string().nullable(),
  priority: SuggestionPrioritySchema,
  status: SuggestionStatusSchema,
  neededQuantity: z.number(),
  currentOnHand: z.number(),
  currentNetPosition: z.number(),
});

export type NoSourceRow = z.infer<typeof NoSourceRowSchema>;

export const ListNoSourceSuggestionsInputSchema = z.object({
  status: SuggestionStatusSchema.optional(),
  divisionId: z.string().uuid().optional(),
  divisionIds: z.array(z.string().uuid()).optional(),
  // Destination-warehouse filter (see ListSupplierSummariesInputSchema).
  warehouseIds: z.array(z.string().uuid()).optional(),
  includeTrashed: z.boolean().optional(),
  search: z.string().optional(),
  page: z.number().int().min(1).optional().default(1),
  pageSize: z.number().int().min(1).max(500).optional().default(50),
});

export const ListNoSourceSuggestionsResponseSchema = z.object({
  rows: z.array(NoSourceRowSchema),
  totalRows: z.number(),
});

export type ListNoSourceSuggestionsInput = z.infer<
  typeof ListNoSourceSuggestionsInputSchema
>;
export type ListNoSourceSuggestionsResponse = z.infer<
  typeof ListNoSourceSuggestionsResponseSchema
>;

// ─── Vendorless-transfer board: source-warehouse cards (tier-1) ─────────────

// One card per source warehouse (the warehouse shipping the stock) — the
// "supplier" analog for pure transfers, which have no vendor.
export const SourceTransferSummaryRowSchema = z.object({
  sourceWarehouseId: z.string().uuid(),
  sourceWarehouseName: z.string(),
  sourceWarehouseType: z.string().nullable(),
  lineCount: z.number(),
  soTriggeredCount: z.number(),
  deficitCount: z.number(),
  // Distinct destination warehouses these transfers go to (= cart count).
  destinationCount: z.number(),
  totalQuantity: z.number(),
});

export type SourceTransferSummaryRow = z.infer<
  typeof SourceTransferSummaryRowSchema
>;

export const ListVendorlessTransferSummariesInputSchema = z.object({
  status: SuggestionStatusSchema.optional(),
  divisionId: z.string().uuid().optional(),
  divisionIds: z.array(z.string().uuid()).optional(),
  // Destination-warehouse filter (see ListSupplierSummariesInputSchema).
  warehouseIds: z.array(z.string().uuid()).optional(),
  includeTrashed: z.boolean().optional(),
  search: z.string().optional(),
});

export const ListVendorlessTransferSummariesResponseSchema = z.object({
  rows: z.array(SourceTransferSummaryRowSchema),
  totalRows: z.number(),
});

export type ListVendorlessTransferSummariesInput = z.infer<
  typeof ListVendorlessTransferSummariesInputSchema
>;
export type ListVendorlessTransferSummariesResponse = z.infer<
  typeof ListVendorlessTransferSummariesResponseSchema
>;

// ─── Supplier summaries with nested cart breakdowns ─────────────────────────

export const CartSummarySchema = z.object({
  shipToWarehouseId: z.string().uuid(),
  shipToWarehouseName: z.string(),
  shipToWarehouseType: z.string().nullable(),
  lineCount: z.number(),
  cartTotal: z.number(),
  movThreshold: z.number().nullable(),
  freightThreshold: z.number().nullable(),
  soTriggeredCount: z.number(),
  // Lines in this cart when its committed total is below the vendor's MOV
  // right now, else 0. Computed live, not from the engine's per-leg flag.
  belowMinimumCount: z.number(),
  // Live forward-buy summary of the cart's committed lines. Null when no line
  // in the cart currently has an uplift as its effective option.
  forwardBuy: CartForwardBuySummarySchema.nullable(),
});

export type CartSummary = z.infer<typeof CartSummarySchema>;

export const SupplierWithCartsSummaryRowSchema = z.object({
  supplierId: z.string().uuid(),
  supplierName: z.string(),
  openSuggestionCount: z.number(),
  totalEstimatedCost: z.number(),
  cartCount: z.number(),
  soTriggeredCount: z.number(),
  deficitCount: z.number(),
  // Lines sitting in this vendor's carts that are below their MOV right now
  // (the sum of the carts' belowMinimumCount).
  belowMinimumCount: z.number(),
  // Lines under this vendor whose recommended action is a transfer (no buy
  // spend). They count toward openSuggestionCount but not toward any cart/MOV.
  transferCount: z.number(),
  // POTF (product-on-the-fly) line count, joined via the suggestion's trigger
  // soli's `metadata.isProductOnTheFly`. Belt's POTF card sorts by this.
  potfLineCount: z.number(),
  // AS400 division codes (e.g. ['RA', 'WG']) covering every destination
  // warehouse this supplier's suggestions touch. Sorted alphabetically.
  // Rendered as small pills on each supplier card so buyers can see the
  // division at a glance when multi-division is selected.
  divisionCodes: z.array(z.string()),
  carts: z.array(CartSummarySchema),
});

export type SupplierWithCartsSummaryRow = z.infer<
  typeof SupplierWithCartsSummaryRowSchema
>;

// A synthetic row representing aggregated vendorless-transfer suggestions
// (resolver bucket=`vendorless` — pure transfers with no buy vendor anywhere
// in their options). Rendered at the bottom of the Actionable list as a single
// "Vendorless transfers" card, treated by the sort stack like any other row.
export const VendorlessTransfersRowSchema = z.object({
  lineCount: z.number(),
  soTriggeredCount: z.number(),
  deficitCount: z.number(),
  potfLineCount: z.number(),
});

export type VendorlessTransfersRow = z.infer<
  typeof VendorlessTransfersRowSchema
>;

export const ListSupplierSummariesWithCartsResponseSchema = z.object({
  rows: z.array(SupplierWithCartsSummaryRowSchema),
  totalRows: z.number(),
  // null when no vendorless transfers exist in scope.
  vendorlessRow: VendorlessTransfersRowSchema.nullable(),
});

export type ListSupplierSummariesWithCartsResponse = z.infer<
  typeof ListSupplierSummariesWithCartsResponseSchema
>;

// ─── Buying orders (one row per proposed PO / TO cart) ──────────────────────

export const BuyingOrderLineTypeSchema = z.enum([
  "back_order",
  "potf",
  "stock",
]);
export const BuyingOrderKindSchema = z.enum(["po", "to"]);
export const BuyingOrderSortSchema = z.enum([
  "priority",
  "value",
  "oldest_so",
  "name",
  "lines",
  "back_order_value",
  "potf_value",
  "stock_value",
  "shareable_value",
]);

// The shared procurement filters (Forecast Summary / Details) the buyer board
// applies to its lines: the destination policy's POP code and stock status,
// the destination's warehouse type, and the forecast grid's SKU / text boxes.
// Same names and shapes as DemandMatrixFilterSchema, so one URL serves both.
const SharedLineFiltersShape = {
  popularityCodes: z.array(z.string()).optional(),
  stockStatuses: z.array(z.string()).optional(),
  warehouseTypes: z.array(z.string()).optional(),
  skuSearch: z.string().optional(),
  textSearch: z.string().optional(),
};

export const BuyingOrderGroupBySchema = z.enum(["vendor_location", "vendor"]);

export const ListBuyingOrdersInputSchema = z.object({
  divisionIds: z.array(z.string().uuid()).min(1),
  // Destination-warehouse scope, same semantics as the supplier summaries.
  warehouseIds: z.array(z.string().uuid()).optional(),
  supplierIds: z.array(z.string().uuid()).optional(),
  // Keep only the TO rows shipping from these source warehouses (the transfer
  // board's Source filter, GEN-3026).
  sourceWarehouseIds: z.array(z.string().uuid()).optional(),
  // Keep only the PO carts shipping to these warehouses: the open cart's row,
  // whose readiness the detail header shows (GEN-3234).
  shipToWarehouseIds: z.array(z.string().uuid()).optional(),
  // Keep only orders holding at least one line of a selected type. Counts on
  // the row still reflect every line.
  lineTypes: z.array(BuyingOrderLineTypeSchema).optional(),
  orderTypes: z.array(BuyingOrderKindSchema).optional(),
  // Keep only lines whose buyer (the policy's buyer code at the line's
  // destination) is listed: orders with no such line drop out, and row counts
  // and dollars cover the kept lines only.
  buyerCodes: z.array(z.string().min(1).max(12)).optional(),
  // The shared procurement line filters narrow lines the same way.
  ...SharedLineFiltersShape,
  // Keep only PO carts whose order readiness (the Order column) is listed,
  // judged per cart before vendor grouping; TO rows drop out.
  readiness: z.array(z.enum(["ready", "warning", "not_ready"])).optional(),
  // Keep only orders holding a pending line at least this many days on order
  // (GEN-3151): days since the engine first raised the line, which resets when
  // the line is ordered (a released line comes back as a new suggestion).
  // Judged per cart before vendor grouping.
  minDaysOnOrder: z.number().int().min(0).optional(),
  // Keep lines a buyer released since the latest engine refresh (GEN-3119),
  // listed but never counted as committed. Omitted = pending lines only.
  includeReleased: z.boolean().optional(),
  // Case-insensitive match on supplier / source / ship-to name or code.
  search: z.string().optional(),
  // vendor_location: one row per cart (vendor x ship-to). vendor: one row per
  // supplier / TO source, summed across ship-tos / destinations.
  groupBy: BuyingOrderGroupBySchema.optional().default("vendor_location"),
  sort: BuyingOrderSortSchema.optional().default("priority"),
  // Defaults per sort: priority/oldest_so/name ascending, value/lines and the
  // per-type dollar sorts descending.
  sortDirection: z.enum(["asc", "desc"]).optional(),
  limit: z.number().int().min(1).max(200).optional().default(100),
  offset: z.number().int().min(0).optional().default(0),
});

/**
 * True when the vendor's only minimum of that kind is a "never" placeholder:
 * the threshold reads as null (the board default applies) and the buyer board
 * skips its buffer band (GEN-2931). Absent means false.
 */
const thresholdPlaceholderFields = {
  movThresholdPlaceholder: z.boolean().optional(),
  freightThresholdPlaceholder: z.boolean().optional(),
};

export const BuyingOrderRowSchema = z.object({
  kind: BuyingOrderKindSchema,
  // PO rows: the supplier. TO rows: null.
  supplierId: z.string().uuid().nullable(),
  supplierName: z.string().nullable(),
  // TO rows: the source warehouse. PO rows: null.
  sourceWarehouseId: z.string().uuid().nullable(),
  sourceWarehouseName: z.string().nullable(),
  sourceWarehouseCode: z.string().nullable(),
  // PO ship-to, or the TO destination. Null in vendor grouping.
  shipToWarehouseId: z.string().uuid().nullable(),
  shipToWarehouseName: z.string().nullable(),
  shipToWarehouseCode: z.string().nullable(),
  // Distinct ship-tos / destinations behind the row (1 per cart).
  locationCount: z.number(),
  lineCount: z.number(),
  backOrderCount: z.number(),
  potfCount: z.number(),
  stockCount: z.number(),
  // What Release would send (GEN-3234): pending lines approved by the buyer
  // or the engine and not in Trouble, with how many miss the vendor MOQ or
  // pack multiple. The row's readiness is judged on these alone. An older
  // backend omits them; zero reads as nothing approved (Not ready).
  approvedLineCount: z.number().default(0),
  approvedBelowMoqLineCount: z.number().default(0),
  approvedBelowOffIncrementLineCount: z.number().default(0),
  // Committed lines per type; the rest of each type's count are uncommitted
  // fill lines that carry no dollars.
  backOrderCommittedCount: z.number(),
  potfCommittedCount: z.number(),
  stockCommittedCount: z.number(),
  // Dollars by line type: the sum of each type's committed line cost (same
  // basis as cartTotal, but over the lines in view, not the whole cart).
  backOrderValue: z.number(),
  potfValue: z.number(),
  stockValue: z.number(),
  // Sharing suggestions (GEN-3151): committed buy lines the engine could also
  // fill by a transfer (the suggestion holds a pure-transfer option), and
  // their dollars: how much of the buy could be internal instead. Zero on TO
  // rows and on buyer-switched transfers, which are internal already.
  // Older backends omit these fields. Missing totals are unknown, not zero.
  shareableCount: z.number().nullable().default(null),
  shareableValue: z.number().nullable().default(null),
  // Lines whose destination policy carries a core-offer label (GEN-3151):
  // GAINS shows one core offer per group, Robert wants the item count.
  // Keep an omitted count unknown during staggered deploys and rollbacks.
  coreLineCount: z.number().nullable().default(null),
  // Lines auto release left for a later day under their source's daily line
  // cap (GEN-3027). Zero on PO rows; omitted by older backends.
  queuedLineCount: z.number().default(0),
  // TO rows: the destination's inbound mode in the published supply network
  // (auto_stage = auto release ships it). Null on PO rows, before a network
  // is published, and in vendor grouping.
  inboundMode: z.enum(["auto_stage", "review"]).nullable().default(null),
  // Vendor grouping: the sum of the vendor's whole-cart totals.
  cartTotal: z.number(),
  // The approved lines' whole-cart dollars, on cartTotal's basis.
  approvedTotal: z.number().default(0),
  // Per-cart minimums; null in vendor grouping.
  movThreshold: z.number().nullable(),
  freightThreshold: z.number().nullable(),
  ...thresholdPlaceholderFields,
  // Earliest order date (YYYY-MM-DD) of the trigger sales orders.
  oldestSalesOrderDate: z.string().nullable(),
  // Days on order of the row's oldest pending line (see minDaysOnOrder); null
  // when the row has no pending line or an older backend omits the field.
  maxDaysOnOrder: z.number().nullable().default(null),
  // Distinct buyer codes of the row's lines, sorted; empty when none is coded.
  buyerCodes: z.array(z.string()),
});

export const ListBuyingOrdersResponseSchema = z.object({
  rows: z.array(BuyingOrderRowSchema),
  totalRows: z.number(),
});

export const ListBuyerCodesInputSchema = z.object({
  divisionIds: z.array(z.string().uuid()).min(1),
  warehouseIds: z.array(z.string().uuid()).optional(),
});

export const ListBuyerCodesResponseSchema = z.object({
  // Distinct buyer codes of the policies in scope, sorted.
  codes: z.array(z.string()),
});

// When the board's data was last refreshed, per selected division (GEN-3149).
export const GetBoardFreshnessInputSchema = z.object({
  divisionIds: z.array(z.string().uuid()).min(1),
});

export const BoardFreshnessDivisionSchema = z.object({
  divisionId: z.string().uuid(),
  divisionName: z.string(),
  // The latest run covering the division: org-wide or scoped to it. Null when
  // none has been recorded.
  latestRun: BoardFreshnessRunSchema.nullable(),
  // When the newest completed run covering the division ended (ISO
  // timestamp): the board's "last updated" time. Null when none completed.
  lastCompletedAt: z.string().nullable(),
  // When the stock feed last wrote the division's newest on-hand snapshot (ISO
  // timestamp); null with no snapshot.
  stockSyncedAt: z.string().nullable(),
});

export const GetBoardFreshnessResponseSchema = z.object({
  divisions: z.array(BoardFreshnessDivisionSchema),
});

export type GetBoardFreshnessInput = z.input<
  typeof GetBoardFreshnessInputSchema
>;
export type BoardFreshnessRun = z.infer<typeof BoardFreshnessRunSchema>;
export type BoardFreshnessDivision = z.infer<
  typeof BoardFreshnessDivisionSchema
>;
export type GetBoardFreshnessResponse = z.infer<
  typeof GetBoardFreshnessResponseSchema
>;
export type ListBuyerCodesInput = z.input<typeof ListBuyerCodesInputSchema>;
export type ListBuyerCodesResponse = z.infer<
  typeof ListBuyerCodesResponseSchema
>;
export type BuyingOrderGroupBy = z.infer<typeof BuyingOrderGroupBySchema>;
export type ListBuyingOrdersInput = z.input<typeof ListBuyingOrdersInputSchema>;
export type BuyingOrderRow = z.infer<typeof BuyingOrderRowSchema>;
export type ListBuyingOrdersResponse = z.infer<
  typeof ListBuyingOrdersResponseSchema
>;

// ─── Cart lines (suggestions within a supplier × ship-to cart) ──────────────

export const CartLineRowSchema = z.object({
  suggestionId: z.string().uuid(),
  productSku: z.string().nullable(),
  productName: z.string(),
  destinationWarehouseId: z.string().uuid(),
  destinationWarehouseName: z.string(),
  trigger: SuggestionTriggerSchema,
  triggerSalesOrderId: z.string().uuid().nullable(),
  triggerSalesOrderNumber: z.string().nullable(),
  // Trouble entries carried by the triggering sales order — today only a
  // customer credit hold (ON_HOLD / CREDIT_HOLD), stamped at DMDTRANS ingest.
  // Null on deficit-triggered lines: those aggregate demand from many
  // customers, so no single customer's hold applies to the line.
  triggerSalesOrderTroubleData: TroubleDataSchema,
  priority: SuggestionPrioritySchema,
  status: SuggestionStatusSchema,
  neededQuantity: z.number(),
  currentOnHand: z.number(),
  currentNetPosition: z.number(),
  policyMin: z.number().nullable(),
  policyMax: z.number().nullable(),
  topOption: z.object({
    optionId: z.string().uuid(),
    action: OptionActionSchema,
    buyQuantity: z.number().nullable(),
    unitCost: z.number().nullable(),
    totalCost: z.number().nullable(),
    rationale: z.string().nullable(),
    belowMinimum: z.boolean(),
    minimumGapAmount: z.number().nullable(),
    transferDestinationWarehouseName: z.string().nullable(),
    transferQuantity: z.number().nullable(),
    // Price-break provenance of the option's buy leg. Null for transfer-only
    // lines and for options the engine did not price (see
    // SuggestionOptionLegSchema.priceBreak).
    priceBreak: LegPriceBreakSchema.nullable(),
    // Forward-buy provenance when this option is an uplift; null otherwise.
    forwardBuy: OptionForwardBuyDetailSchema.nullable(),
  }),
});

export type CartLineRow = z.infer<typeof CartLineRowSchema>;

export const GetCartLinesInputSchema = z.object({
  supplierId: z.string().uuid(),
  shipToWarehouseId: z.string().uuid(),
  status: SuggestionStatusSchema.optional(),
  search: z.string().optional(),
  page: z.number().int().min(1).optional().default(1),
  pageSize: z.number().int().min(1).max(500).optional().default(50),
});

export type GetCartLinesInput = z.infer<typeof GetCartLinesInputSchema>;

export const GetCartLinesResponseSchema = z.object({
  supplierId: z.string().uuid(),
  supplierName: z.string(),
  shipToWarehouseId: z.string().uuid(),
  shipToWarehouseName: z.string(),
  cartTotal: z.number(),
  movThreshold: z.number().nullable(),
  freightThreshold: z.number().nullable(),
  rows: z.array(CartLineRowSchema),
  totalRows: z.number(),
});

export type GetCartLinesResponse = z.infer<typeof GetCartLinesResponseSchema>;

// ─── Supplier assignments (all lines + carts for one supplier) ──────────────

export const CartAssignmentSchema = z.object({
  shipToWarehouseId: z.string().uuid(),
  shipToWarehouseName: z.string(),
  shipToWarehouseType: z.string().nullable(),
  movThreshold: z.number().nullable(),
  freightThreshold: z.number().nullable(),
  ...thresholdPlaceholderFields,
});

export type CartAssignment = z.infer<typeof CartAssignmentSchema>;

// The line destination's own customer sales over the last complete months.
// Unavailable when a source cannot be counted once.
export const LineUsageSchema = z.discriminatedUnion("availability", [
  z.object({
    availability: z.literal("available"),
    // Last complete month, YYYY-MM-01.
    throughMonth: z.iso.date(),
    months: z.number().int().positive(),
    // Net customer sales at the destination itself.
    ownQuantity: z.number(),
    // Summed over every location that rolls up to the destination through the
    // published supply network. Null when none does (shown as a dash, never 0).
    dependent: z
      .object({
        quantity: z.number(),
        locationCount: z.number().int().positive(),
      })
      .nullable(),
  }),
  z.object({
    availability: z.literal("unavailable"),
    reason: z.enum([
      "source_overlap_conflict",
      "missing_source_identity",
      "source_incompatible",
    ]),
  }),
]);

export type LineUsage = z.infer<typeof LineUsageSchema>;

export const AssignmentLineSchema = z.object({
  suggestionId: z.string().uuid(),
  /** `im_products_v3.key` — use for fulfillment candidate lookups (may differ from display SKU). */
  productV3Key: z.string().nullable(),
  productSku: z.string().nullable(),
  productName: z.string(),
  // Item description from the product master; null when none is on file (the
  // board leaves it blank rather than repeating the SKU).
  productDescription: z.string().nullable(),
  destinationWarehouseId: z.string().uuid(),
  destinationWarehouseName: z.string(),
  destinationWarehouseType: z.string().nullable(),
  destinationWarehouseHubName: z.string().nullable(),
  trigger: SuggestionTriggerSchema,
  triggerSalesOrderId: z.string().uuid().nullable(),
  triggerSalesOrderNumber: z.string().nullable(),
  // Trouble entries carried by the triggering sales order — today only a
  // customer credit hold (ON_HOLD / CREDIT_HOLD), stamped at DMDTRANS ingest.
  // Null on deficit-triggered lines: those aggregate demand from many
  // customers, so no single customer's hold applies to the line.
  triggerSalesOrderTroubleData: TroubleDataSchema,
  // The triggering order's customer and the date it is needed (GEN-2938), so
  // the SO lines under one item's need tell apart. Null on deficit lines, and
  // from a server that predates them.
  triggerSalesOrderCustomerName: z.string().nullable().default(null),
  /** `YYYY-MM-DD`: the order's delivery date, else its order date. */
  triggerSalesOrderRequestedDate: z.string().nullable().default(null),
  priority: SuggestionPrioritySchema,
  status: SuggestionStatusSchema,
  neededQuantity: z.number(),
  currentOnHand: z.number(),
  currentNetPosition: z.number(),
  reservedQuantity: z.number(),
  policyMin: z.number().nullable(),
  policyMax: z.number().nullable(),
  popularityCode: z.string().nullable(),
  // Buyer code of the policy at the line's destination (GAINS Receiving Planner).
  buyerCode: z.string().nullable(),
  stockStatus: z.string().nullable(),
  // Core-offer program label of the policy at the line's destination (AS/400
  // as_stock.salph2); null when the item is in no program there (GEN-3151).
  // Older backends omit the label; the board leaves it blank in that case.
  coreOffer: z.string().nullable().default(null),
  // Transfer board (GEN-3026): what the line's source can give for this item,
  // its stock above the source's release floor in the published network
  // (docs/procurement/stock-release-floors.md). Null on vendor lines.
  sourceAvailableQuantity: z.number().nullable().default(null),
  // When auto release left the line for a later day under its source's daily
  // line cap (ISO timestamp, GEN-3027); null when it never was.
  autoReleaseQueuedAt: z.string().nullable().default(null),
  // Item weight in pounds per stocking unit, from the product master. Null when
  // the item has no weight on file (or in a unit we cannot convert).
  unitWeightLbs: z.number().nullable(),
  shipToWarehouseId: z.string().uuid().nullable(),
  // The engine's own calculated quantity, before any buyer override; null
  // when the engine wrote none. Differs from the option quantity once a buyer
  // has adjusted the line.
  calculatedQuantity: z.number().nullable(),
  // Engine's committed action for this line — null when the line is a fill
  // candidate (deficit trigger without an explicit chosen option). Used by the
  // UI to dim rows that are not actually committed to be ordered.
  recommendedAction: OptionActionSchema.nullable(),
  // The line's approval state: only an approved line goes on an order, and a
  // Trouble line never does. Independent of recommendedAction, which is the
  // engine's suggested state.
  approvalState: LineApprovalStateSchema,
  // Why the line is in Trouble; null unless approvalState is 'trouble'.
  troubleReason: LineTroubleReasonSchema.nullable(),
  // The buyer switched this line from the vendor to a transfer (GEN-3023): it
  // stays in this vendor cart and is released as a transfer order with it.
  isBuyerSwitchedTransfer: z.boolean(),
  // Null only when the line has no productV3Key.
  usage: LineUsageSchema.nullable(),
  // Vendor quantity price breaks loaded for this SKU at this cart's ship-to.
  vendorPriceBreakTiers: z.array(VendorPriceBreakTierSchema),
  // Active contract prices at the line's buy quantity; empty when none apply.
  buyContractPriceOptions: z.array(BuyContractPriceOptionSchema),
  // Buyer cost: the system (list) unit cost of the catalog row the line buys
  // on, before contract prices and price breaks. Null without a catalog row.
  buyerUnitCost: z.number().nullable(),
  // The buyer's own terms for the line (GEN-2943).
  buyTerms: LineBuyTermsSchema,
  topOption: z
    .object({
      optionId: z.string().uuid(),
      action: OptionActionSchema,
      buyQuantity: z.number().nullable(),
      vendorMoqQuantity: z.number().nullable(),
      vendorIncrementQuantity: z.number().nullable(),
      unitCost: z.number().nullable(),
      totalCost: z.number().nullable(),
      rationale: z.string().nullable(),
      belowMinimum: z.boolean(),
      minimumGapAmount: z.number().nullable(),
      transferDestinationWarehouseName: z.string().nullable(),
      transferQuantity: z.number().nullable(),
      sourceWarehouseId: z.string().uuid().nullable(),
      // Name of the transfer leg's source warehouse, for the Source column.
      sourceWarehouseName: z.string().nullable(),
      // Buy legs: measured observed lead time when a rung answered, else the
      // vendor's declared (GILEAD) value. Transfer legs: the flat transit
      // assumption. Null on buyer-composed plans (legs persist no estimate).
      estimatedLeadTimeDays: z.number().nullable(),
      // Live-resolved active lead-time override for this product x destination
      // warehouse; see GetSuggestionDetailResponseSchema.leadTimeOverrideDays.
      leadTimeOverrideDays: z.number().nullable(),
      // Price-break provenance of the option's buy leg, for the board's
      // next-tier nudge. Null on transfer-only lines and on options the engine
      // did not price (see SuggestionOptionLegSchema.priceBreak).
      priceBreak: LegPriceBreakSchema.nullable(),
      // Forward-buy provenance when this option is an uplift, for the row's
      // base + added = total line and its coverage chip. Null otherwise.
      forwardBuy: OptionForwardBuyDetailSchema.nullable(),
      // A substitute or make plan's legs carry another product (the
      // supplying product, or the make's components), so the cart's vendor,
      // cost and quantity belong to that product, not the line's.
      planKind: z.enum(OPTION_PLAN_KINDS),
    })
    .nullable(),
});

export type AssignmentLine = z.infer<typeof AssignmentLineSchema>;

export const GetSupplierAssignmentsInputSchema = z.object({
  supplierId: z.string().uuid(),
  divisionId: z.string().uuid().optional(),
  divisionIds: z.array(z.string().uuid()).optional(),
  // Destination-warehouse filter (see ListSupplierSummariesInputSchema).
  warehouseIds: z.array(z.string().uuid()).optional(),
  includeTrashed: z.boolean().optional(),
  // Also list lines a buyer released since the latest engine refresh, as
  // `released` (GEN-3119).
  includeReleased: z.boolean().optional(),
  status: SuggestionStatusSchema.optional(),
  // Load only the cart shipping to this warehouse (the detail pane's cart).
  shipToWarehouseId: z.string().uuid().optional(),
  // Keep only lines whose buyer (the policy's buyer code at the line's
  // destination) is listed; carts left with no line drop out. The shared
  // procurement line filters narrow lines the same way.
  buyerCodes: z.array(z.string().min(1).max(12)).optional(),
  ...SharedLineFiltersShape,
});

export type GetSupplierAssignmentsInput = z.infer<
  typeof GetSupplierAssignmentsInputSchema
>;

export const GetSupplierAssignmentsResponseSchema = z.object({
  supplierId: z.string().uuid(),
  supplierName: z.string(),
  carts: z.array(CartAssignmentSchema),
  lines: z.array(AssignmentLineSchema),
});

export type GetSupplierAssignmentsResponse = z.infer<
  typeof GetSupplierAssignmentsResponseSchema
>;

// ─── Source-warehouse transfer assignments (tier-2 for the transfer board) ──

export const GetSourceTransferAssignmentsInputSchema = z.object({
  sourceWarehouseId: z.string().uuid(),
  divisionId: z.string().uuid().optional(),
  divisionIds: z.array(z.string().uuid()).optional(),
  warehouseIds: z.array(z.string().uuid()).optional(),
  includeTrashed: z.boolean().optional(),
  // Also list lines released since the latest engine refresh, as the Orders
  // row counts them (GEN-3119, GEN-3026).
  includeReleased: z.boolean().optional(),
  status: SuggestionStatusSchema.optional(),
  // Load only the lines going to this destination (the detail pane's cart).
  destinationWarehouseId: z.string().uuid().optional(),
  // The board's line filters, as on the supplier detail (GEN-3026).
  buyerCodes: z.array(z.string().min(1).max(12)).optional(),
  ...SharedLineFiltersShape,
});

// Reuses CartAssignment + AssignmentLine so the frontend renders transfers with
// the same cart components as the supplier board. Carts are keyed on the
// destination warehouse; each line's `shipToWarehouseId` is its destination.
export const GetSourceTransferAssignmentsResponseSchema = z.object({
  sourceWarehouseId: z.string().uuid(),
  sourceWarehouseName: z.string(),
  carts: z.array(CartAssignmentSchema),
  lines: z.array(AssignmentLineSchema),
});

export type GetSourceTransferAssignmentsInput = z.infer<
  typeof GetSourceTransferAssignmentsInputSchema
>;
export type GetSourceTransferAssignmentsResponse = z.infer<
  typeof GetSourceTransferAssignmentsResponseSchema
>;

// ─── Apply option (set chosen fulfillment option for a suggestion) ──────────

export const ApplyOptionInputSchema = z.object({
  suggestionId: z.string().uuid(),
  // null = clear the selection (revert to the engine's recommended default).
  optionId: z.string().uuid().nullable(),
});

export type ApplyOptionInput = z.infer<typeof ApplyOptionInputSchema>;

export const ApplyOptionResponseSchema = z.object({
  suggestionId: z.string().uuid(),
  chosenOptionId: z.string().uuid().nullable(),
});

export type ApplyOptionResponse = z.infer<typeof ApplyOptionResponseSchema>;

// ─── Apply a price-break nudge (raise a buy line to the next break) ─────────

// Names the line and nothing else: the server re-derives the quantity and the
// price from the vendor's catalog, so a stale or tampered client cannot order
// off the vendor's lattice or at the old tier's price.
export const ApplyPriceBreakInputSchema = z.object({
  suggestionId: z.string().uuid(),
  // The option the nudge was offered on, normally the line's effective option.
  optionId: z.string().uuid(),
});

export type ApplyPriceBreakInput = z.infer<typeof ApplyPriceBreakInputSchema>;

export const ApplyPriceBreakResponseSchema = z.object({
  suggestionId: z.string().uuid(),
  // The buyer-authored option the nudge created, now the chosen option.
  optionId: z.string().uuid(),
  // What was applied: the next break rounded up to the vendor's MOQ and order
  // multiple, and the unit cost the vendor charges at that quantity.
  quantity: z.number(),
  unitCost: z.number(),
});

export type ApplyPriceBreakResponse = z.infer<
  typeof ApplyPriceBreakResponseSchema
>;

// ─── Set a line's approval state ────────────────────────────────────────────

// The states a buyer sets directly. Rejected goes through setSuggestionStatus
// (trash / restore), and a credit hold is read from the customer, never set.
export const SettableLineApprovalStateSchema = z.enum([
  "unapproved",
  "approved",
  "trouble",
]);

export type SettableLineApprovalState = z.infer<
  typeof SettableLineApprovalStateSchema
>;

export const SetSuggestionApprovalStateInputSchema = z.object({
  suggestionIds: z.array(z.string().uuid()).min(1).max(500),
  state: SettableLineApprovalStateSchema,
});

export type SetSuggestionApprovalStateInput = z.infer<
  typeof SetSuggestionApprovalStateInputSchema
>;

export const SetSuggestionApprovalStateResponseSchema = z.object({
  // Only the pending suggestions in the caller's organization that were updated.
  suggestionIds: z.array(z.string().uuid()),
});

export type SetSuggestionApprovalStateResponse = z.infer<
  typeof SetSuggestionApprovalStateResponseSchema
>;

// ─── A buyer's terms for buy lines (GEN-2943) ───────────────────────────────

export const SetLineBuyTermsInputSchema = z.object({
  suggestionIds: z.array(z.string().uuid()).min(1).max(10_000),
  receiveCostAdjustment: CostConstraintAdjustmentSchema.optional(),
  // An omitted field keeps its value; null clears it.
  terms: LineBuyTermsSchema.omit({ costConstraintUnitCost: true }).partial(),
});

export type SetLineBuyTermsInput = z.infer<typeof SetLineBuyTermsInputSchema>;

export const SetLineBuyTermsResponseSchema = z.object({
  // Only the pending suggestions in the caller's organization that were updated.
  suggestionIds: z.array(z.string().uuid()),
});

export type SetLineBuyTermsResponse = z.infer<
  typeof SetLineBuyTermsResponseSchema
>;

export const SetRevisedUnitCostsInputSchema = z.object({
  // One revised cost per line; null resets the line to its system cost.
  // "Whole order" sends every buy line of a cart, and a cart can hold
  // thousands, so the cap is set well above the board's largest cart.
  costs: z
    .array(
      z.object({
        suggestionId: z.string().uuid(),
        revisedUnitCost: LineBuyTermsSchema.shape.revisedUnitCost,
      }),
    )
    .min(1)
    .max(10_000),
});

export type SetRevisedUnitCostsInput = z.infer<
  typeof SetRevisedUnitCostsInputSchema
>;

// ─── Trash / restore a suggestion (set status pending ↔ rejected) ───────────

export const SetSuggestionStatusInputSchema = z.object({
  suggestionId: z.string().uuid(),
  // 'rejected' trashes the line (hidden from the board); 'pending' restores it.
  // Only these two transitions are allowed — never touches accepted/ordered.
  status: z.enum(["pending", "rejected"]),
});

export type SetSuggestionStatusInput = z.infer<
  typeof SetSuggestionStatusInputSchema
>;

export const SetSuggestionStatusResponseSchema = z.object({
  suggestionId: z.string().uuid(),
  status: z.enum(["pending", "rejected"]),
});

// Trash or restore many lines at once (GEN-2938: every open SO line of one
// item's need). Same two transitions as SetSuggestionStatusInputSchema.
export const SetSuggestionStatusesInputSchema = z.object({
  suggestionIds: z.array(z.string().uuid()).min(1).max(500),
  status: z.enum(["pending", "rejected"]),
});

export type SetSuggestionStatusesInput = z.infer<
  typeof SetSuggestionStatusesInputSchema
>;

export const SetSuggestionStatusesResponseSchema = z.object({
  /** The lines that changed; others were not in the starting state. */
  suggestionIds: z.array(z.string().uuid()),
  status: z.enum(["pending", "rejected"]),
});

export type SetSuggestionStatusesResponse = z.infer<
  typeof SetSuggestionStatusesResponseSchema
>;

export type SetSuggestionStatusResponse = z.infer<
  typeof SetSuggestionStatusResponseSchema
>;

// ─── Override (buyer adds a custom user-authored option) ────────────────────

export const OverrideOptionInputSchema = z
  .object({
    suggestionId: z.string().uuid(),
    action: OverrideActionSchema,
    supplierId: z.string().uuid().nullable().optional(),
    shipToWarehouseId: z.string().uuid().nullable().optional(),
    buyQuantity: z.number().nonnegative().nullable().optional(),
    // A fallback only. The server prices a catalogued buy itself, on the
    // ladder the engine would use today (cost agreements at the ship-to, then
    // price breaks, then list), and keeps this value only when the supplier
    // has no row for the SKU.
    unitCost: z.number().nonnegative().nullable().optional(),
    // Pin the buy to this contract's unit cost when the server prices the line.
    costAgreementId: z.string().uuid().nullable().optional(),
    sourceWarehouseId: z.string().uuid().nullable().optional(),
    transferQuantity: z.number().nonnegative().nullable().optional(),
    // Whether the new option becomes the suggestion's chosen option (default true).
    setChosen: z.boolean().optional().default(true),
  })
  // fallow-ignore-next-line complexity
  .superRefine((v, ctx) => {
    const isBuy = v.action === "buy" || v.action === "buy_and_transfer";
    const isTransfer =
      v.action === "transfer" || v.action === "buy_and_transfer";
    // Buy legs need a vendor + quantity (ship-to defaults to the suggestion's
    // destination server-side). Transfer legs need a source + quantity. Missing
    // fields would persist an option the board can't place.
    if (isBuy && v.supplierId == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["supplierId"],
        message: "supplierId is required for buy / buy_and_transfer",
      });
    }
    if (isBuy && (v.buyQuantity == null || v.buyQuantity <= 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["buyQuantity"],
        message: "buyQuantity is required for buy / buy_and_transfer",
      });
    }
    if (isTransfer && v.sourceWarehouseId == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["sourceWarehouseId"],
        message:
          "sourceWarehouseId is required for transfer / buy_and_transfer",
      });
    }
    if (isTransfer && (v.transferQuantity == null || v.transferQuantity <= 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["transferQuantity"],
        message: "transferQuantity is required for transfer / buy_and_transfer",
      });
    }
  });

export type OverrideOptionInput = z.infer<typeof OverrideOptionInputSchema>;

export const OverrideOptionResponseSchema = z.object({
  suggestionId: z.string().uuid(),
  optionId: z.string().uuid(),
  chosenOptionId: z.string().uuid().nullable(),
});

export type OverrideOptionResponse = z.infer<
  typeof OverrideOptionResponseSchema
>;

/**
 * A new stock line for a need's overflow (GEN-2938): `suggestionId` names one
 * of the need's lines, whose item and location the new line takes, and the
 * rest is the plan it starts with, as an override would set it (`setChosen`
 * is ignored: the plan is always chosen).
 */
export const CreateOverflowLineInputSchema = OverrideOptionInputSchema;

export type CreateOverflowLineInput = z.infer<
  typeof CreateOverflowLineInputSchema
>;

export const CreateOverflowLineResponseSchema = z.object({
  /** The new line's id. */
  suggestionId: z.string().uuid(),
  optionId: z.string().uuid(),
});

export type CreateOverflowLineResponse = z.infer<
  typeof CreateOverflowLineResponseSchema
>;

// ─── Forward buy: remove it from one vendor cart ────────────────────────────

/** One vendor cart (supplier x ship-to); the organization is the session's. */
export const RemoveForwardBuyInputSchema = z.object({
  supplierId: z.string().uuid(),
  shipToWarehouseId: z.string().uuid(),
});

export type RemoveForwardBuyInput = z.infer<typeof RemoveForwardBuyInputSchema>;

export const RemoveForwardBuyResponseSchema = z.object({
  /** Pending lines whose uplift was removed and whose baseline choice came back. */
  suggestionsRestored: z.number(),
  /** Lines forward buying pulled forward, deleted outright with their uplift. */
  suggestionsRemoved: z.number(),
  /** Uplift options deleted (their legs go with them). */
  optionsDeleted: z.number(),
});

export type RemoveForwardBuyResponse = z.infer<
  typeof RemoveForwardBuyResponseSchema
>;

// ─── Composite plan builder (multi-leg buyer override) ─────────────────────

export const ComposePlanLegSchema = z
  .object({
    legType: ComposePlanLegTypeSchema,
    quantity: z.number().positive(),
    // buy fields
    supplierId: z.string().uuid().nullable().optional(),
    shipToWarehouseId: z.string().uuid().nullable().optional(),
    unitCost: z.number().nonnegative().nullable().optional(),
    // transfer fields
    sourceWarehouseId: z.string().uuid().nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.legType === "buy" && !v.supplierId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["supplierId"],
        message: "buy leg requires supplierId",
      });
    }
    if (v.legType === "transfer" && !v.sourceWarehouseId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["sourceWarehouseId"],
        message: "transfer leg requires sourceWarehouseId",
      });
    }
  });

export const ComposePlanInputSchema = z
  .object({
    suggestionId: z.string().uuid(),
    legs: z.array(ComposePlanLegSchema).min(1),
    setChosen: z.boolean().prefault(true),
  })
  .superRefine((value, ctx) => {
    if (!hasAtMostOneExplicitBuyCart(value.legs)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["legs"],
        message: SINGLE_BUY_CART_ERROR_MESSAGE,
      });
    }
  });

export type ComposePlanInput = z.infer<typeof ComposePlanInputSchema>;
export type ComposePlanLeg = z.infer<typeof ComposePlanLegSchema>;

export const ComposePlanResponseSchema = z.object({
  suggestionId: z.string().uuid(),
  optionId: z.string().uuid(),
  chosenOptionId: z.string().uuid().nullable(),
});

export type ComposePlanResponse = z.infer<typeof ComposePlanResponseSchema>;

// ─── Fulfillment candidates (override pickers) ──────────────────────────────

export const FulfillmentCandidatesInputSchema = z.object({
  // The SKU is the only product identity a suggestion carries; the override
  // picker asks for physical inventory by it.
  productV3Key: z.string().min(1),
  destinationWarehouseId: z.string().uuid(),
  // Quote each vendor at this quantity, sized onto its ladder (GEN-3246).
  // Absent: the smallest orderable quantity.
  buyQuantity: z.number().positive().optional(),
  // Also list vendors bought from before that have no catalog row, unpriced.
  includeUnpriced: z.boolean().optional(),
});

export type FulfillmentCandidatesInput = z.infer<
  typeof FulfillmentCandidatesInputSchema
>;

export const FulfillmentCandidatesResponseSchema = z.object({
  // Highest on-hand first. The picker shows the short code and the division
  // beside the quantity, since location names repeat across divisions
  // (GEN-3148).
  transferSources: z.array(
    z.object({
      warehouseId: z.string().uuid(),
      warehouseName: z.string(),
      // The AS400 location code (e.g. `RDDCO`); null when not synced.
      warehouseCode: z.string().nullable(),
      divisionName: z.string().nullable(),
      // The AS400 division code (e.g. `RA`); null without a division or code.
      divisionCode: z.string().nullable(),
      onHandQuantity: z.number(),
    }),
  ),
  vendors: z.array(
    z.object({
      supplierId: z.string().uuid(),
      supplierName: z.string(),
      // The vendor's unit cost at `quotedQuantity`, on the ladder the engine
      // prices with today: a cost agreement eligible at the ship-to, else the
      // row's price breaks, else its list cost. Null when `priced` is false.
      unitPrice: z.number().nullable(),
      // The MOQ and multiple of the rung that priced `unitPrice`: the
      // agreement's when one wins, else the supplier product row's.
      minimumOrderQuantity: z.number().nullable(),
      incrementalOrderQuantity: z.number().nullable(),
      // MOV of the vendor's cart at `shipToWarehouseId`.
      movThreshold: z.number().nullable(),
      // The input's buy quantity sized onto the vendor's ladder.
      quotedQuantity: z.number().nullable(),
      costSource: LegCostSourceSchema.nullable(),
      // Where the vendor's buy ships: the destination, or the supply network
      // source it pivots through when no catalog row reaches the destination.
      shipToWarehouseId: z.string().uuid(),
      pivotsThroughWarehouseId: z.string().uuid().nullable(),
      // Measured from this vendor's own receipts of the SKU (most specific
      // rung with 5+ receipts), else the catalog row's, else none.
      leadTime: z.object({
        days: z.number().nullable(),
        source: z.enum(["measured_vendor", "catalog", "none"]),
        grain: z
          .enum(["vendor_item_loc", "vendor_item", "vendor_item_national"])
          .nullable(),
        n: z.number().int().nullable(),
      }),
      // Orders of this SKU from the vendor in the lookback; null when none.
      history: z
        .object({
          lastOrderedAt: z.iso.date(),
          orderCount: z.number().int(),
          atThisLocation: z.boolean(),
        })
        .nullable(),
      // False for a vendor bought from before with no catalog row now.
      priced: z.boolean(),
    }),
  ),
});

export type FulfillmentCandidatesResponse = z.infer<
  typeof FulfillmentCandidatesResponseSchema
>;

// ─── Per-need decision-support insights (for the composite-builder modal) ──

export const GetNeedInsightsInputSchema = z.object({
  suggestionId: z.string().uuid(),
});

export type GetNeedInsightsInput = z.infer<typeof GetNeedInsightsInputSchema>;

export const NeedInventoryRowSchema = z.object({
  warehouseId: z.string().uuid(),
  warehouseName: z.string(),
  divisionName: z.string().nullable(),
  type: z.string().nullable(),
  onHandQuantity: z.number(),
  reservedQuantity: z.number(),
  availableQuantity: z.number(),
  isDestination: z.boolean(),
});
export type NeedInventoryRow = z.infer<typeof NeedInventoryRowSchema>;

export const NeedSalesPointSchema = z.object({
  monthStart: z.string(),
  // The plotted total: this SKU's own sales plus `attributedQuantity`.
  // Unchanged in meaning for every existing consumer — without sales-history
  // transfer rules it is still just this SKU's own sales — and a consumer that
  // wants the native half alone subtracts `attributedQuantity` from it.
  quantity: z.number(),
  // Quantity a sales-history transfer rule attributed here from another SKU or
  // another warehouse. Zero unless a rule lands on the requested coordinate.
  attributedQuantity: z.number(),
  // How many distinct sales orders that month's NATIVE quantity came from.
  // Units alone hide the difference between one big order and thirty small
  // ones, which is the difference between a one-off and a standing pattern
  // worth stocking for. Attributed quantity adds no orders — those belong to
  // another SKU's provenance.
  orderCount: z.number(),
});
export type NeedSalesPoint = z.infer<typeof NeedSalesPointSchema>;

// One forward month of the demand-forecast curve (the forecasting engine's
// output, im_demand_forecasts). `quantity` is the projected demand; `lower`/
// `upper` are the optional confidence band (null until the engine emits one).
// `monthStart` = YYYY-MM-01, same convention as NeedSalesPoint so the two
// series stitch onto one continuous timeline in the chart.
export const ForecastPointSchema = z.object({
  monthStart: z.string(),
  quantity: z.number(),
  lower: z.number().nullable(),
  upper: z.number().nullable(),
});
export type ForecastPoint = z.infer<typeof ForecastPointSchema>;

export const GetNeedInsightsResponseSchema = z.object({
  suggestionId: z.string().uuid(),
  destinationWarehouseId: z.string().uuid(),
  // The destination's policy, for reading its extension fields.
  inventoryPolicyId: z.string().uuid().nullable(),
  popularityCode: z.string().nullable(),
  inventoryByWarehouse: z.array(NeedInventoryRowSchema),
  salesHistory: z.array(NeedSalesPointSchema),
  // Forward demand-forecast curve at the destination (active run only). Empty
  // when no forecast has been computed for this SKU×warehouse yet.
  forecast: z.array(ForecastPointSchema),
});
export type GetNeedInsightsResponse = z.infer<
  typeof GetNeedInsightsResponseSchema
>;

// Product-scoped variant of the buyer's-menu insights — same payload as
// GetNeedInsights minus `suggestionId`, driven directly by a (SKU,
// destination-warehouse) pair. Used where there is no replenishment suggestion
// to key off (e.g. the outgoing-transfers line drill-in).
export const GetProductInsightsInputSchema = z.object({
  productV3Key: z.string().min(1),
  destinationWarehouseId: z.string().uuid(),
});
export type GetProductInsightsInput = z.infer<
  typeof GetProductInsightsInputSchema
>;

export const GetProductInsightsResponseSchema = z.object({
  destinationWarehouseId: z.string().uuid(),
  // The destination's policy, for reading its extension fields.
  inventoryPolicyId: z.string().uuid().nullable(),
  popularityCode: z.string().nullable(),
  inventoryByWarehouse: z.array(NeedInventoryRowSchema),
  salesHistory: z.array(NeedSalesPointSchema),
  forecast: z.array(ForecastPointSchema),
});
export type GetProductInsightsResponse = z.infer<
  typeof GetProductInsightsResponseSchema
>;

// Standalone product sales-history fetch (not tied to a replenishment
// suggestion). Powers the item-detail page chart.
export const GetProductSalesHistoryInputSchema = z.object({
  /** `im_products_v3.key` (the SKU). The service reads sales lines by it. */
  productV3Key: z.string().min(1),
  // Legacy single-value form. `warehouseIds` is preferred and wins when both
  // are supplied; both accepted for back-compat with existing call sites.
  warehouseId: z.string().uuid().nullable().optional(),
  warehouseIds: z.array(z.string().uuid()).nullable().optional(),
  monthsBack: z.number().int().min(1).max(60).optional().default(12),
});
export type GetProductSalesHistoryInput = z.infer<
  typeof GetProductSalesHistoryInputSchema
>;

export const GetProductSalesHistoryResponseSchema = z.object({
  productV3Key: z.string(),
  warehouseId: z.string().uuid().nullable(),
  salesHistory: z.array(NeedSalesPointSchema),
  // Forward demand-forecast curve over the same warehouse scope (active run per
  // warehouse, summed). Empty when no forecast exists for the selection.
  forecast: z.array(ForecastPointSchema),
});
export type GetProductSalesHistoryResponse = z.infer<
  typeof GetProductSalesHistoryResponseSchema
>;

// Trailing N-month movement history at a (product, optional warehouse). 4
// series: outbound = sales + transfers-out; inbound = buys + transfers-in.
// Same scoping rules as the sales-history endpoint.
export const GetProductMovementHistoryInputSchema = z.object({
  /** `im_products_v3.key` (the SKU): the only identity these series are keyed on. */
  productV3Key: z.string().min(1),
  warehouseId: z.string().uuid().nullable().optional(),
  warehouseIds: z.array(z.string().uuid()).nullable().optional(),
  monthsBack: z.number().int().min(1).max(60).optional().default(12),
});
export type GetProductMovementHistoryInput = z.infer<
  typeof GetProductMovementHistoryInputSchema
>;

export const MovementHistoryPointSchema = z.object({
  monthStart: z.string(),
  sales: z.number(),
  transfersOut: z.number(),
  buys: z.number(),
  transfersIn: z.number(),
});
export type MovementHistoryPoint = z.infer<typeof MovementHistoryPointSchema>;

// One row per (warehouse, month) for the month×location breakdown table.
// `warehouseId` is null for movements not tied to a location (e.g. AS400 buys
// with no ship-to warehouse), surfaced as an "unassigned" location row.
export const MovementByLocationRowSchema = z.object({
  warehouseId: z.string().uuid().nullable(),
  warehouseName: z.string().nullable(),
  divisionName: z.string().nullable(),
  monthStart: z.string(),
  sales: z.number(),
  transfersOut: z.number(),
  buys: z.number(),
  transfersIn: z.number(),
});
export type MovementByLocationRow = z.infer<typeof MovementByLocationRowSchema>;

export const GetProductMovementHistoryResponseSchema = z.object({
  productV3Key: z.string(),
  warehouseId: z.string().uuid().nullable(),
  movements: z.array(MovementHistoryPointSchema),
  byLocation: z.array(MovementByLocationRowSchema),
});
export type GetProductMovementHistoryResponse = z.infer<
  typeof GetProductMovementHistoryResponseSchema
>;

// End-of-month historical on-hand at a (product, optional set of warehouses).
// Used as an overlay line on the sales + movement charts.
export const GetProductOnHandHistoryInputSchema = z.object({
  /**
   * `im_products_v3.key` (the SKU), and deliberately the only identity here.
   *
   * Unlike the sales/movement series, this one reads a single table whose rows
   * ALWAYS carry a v3 key: measured 1,184,685 of 1,184,685 on a pre-cutover
   * database (which carries both keys) and 11,959,440 of 11,959,440 on a
   * post-cutover one (which carries only v3). And no writer can produce a
   * v2-only snapshot row, because `upsertInventorySnapshotsFromRows` skips a
   * SKU that does not resolve to a v3 key. So a v2 branch could never reach a
   * row this one misses.
   */
  productV3Key: z.string().min(1),
  warehouseIds: z.array(z.string().uuid()).nullable().optional(),
  monthsBack: z.number().int().min(1).max(60).optional().default(12),
});
export type GetProductOnHandHistoryInput = z.infer<
  typeof GetProductOnHandHistoryInputSchema
>;

export const OnHandHistoryPointSchema = z.object({
  monthStart: z.string(),
  onHand: z.number(),
});
export type OnHandHistoryPoint = z.infer<typeof OnHandHistoryPointSchema>;

export const GetProductOnHandHistoryResponseSchema = z.object({
  productV3Key: z.string(),
  onHand: z.array(OnHandHistoryPointSchema),
});
export type GetProductOnHandHistoryResponse = z.infer<
  typeof GetProductOnHandHistoryResponseSchema
>;

// ─── Ad hoc order creation ─────────────────────────────────────────────────

const MAX_PERSISTED_MONEY_CENTS = 999_999_999_999_999n;

function fitsPersistedMoneyAmount(value: string): boolean {
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(value);
  const whole = match?.[1];
  if (!whole) return false;
  const fraction = (match[2] ?? "").padEnd(2, "0");
  return BigInt(whole) * 100n + BigInt(fraction) <= MAX_PERSISTED_MONEY_CENTS;
}

const PositiveMoneyStringSchema = z
  .string()
  .regex(/^\d+(\.\d{1,2})?$/, "Must have at most two decimal places")
  .refine(fitsPersistedMoneyAmount, "Must fit the stored monetary amount");

const TransferQuantitySchema = z
  .number()
  .positive()
  .max(99_999_999_999_999)
  .multipleOf(0.0001);

export const CreateAdHocPurchaseOrderLineSchema = z.object({
  supplierProductId: z.string().uuid(),
  quantity: z.number().int().positive().max(2_147_483_647),
  unitPrice: PositiveMoneyStringSchema,
});

export const CreateAdHocPurchaseOrderInputSchema = z.object({
  idempotencyKey: z.string().uuid(),
  supplierId: z.string().uuid(),
  shipToWarehouseId: z.string().uuid(),
  acknowledgeBelowMov: z.boolean().prefault(false),
  lines: z.array(CreateAdHocPurchaseOrderLineSchema).min(1).max(200),
});

export type CreateAdHocPurchaseOrderInput = z.infer<
  typeof CreateAdHocPurchaseOrderInputSchema
>;

export const AdHocPurchaseOrderApprovalDispatchStatusSchema = z.enum([
  "pending",
  "dispatching",
  "dispatched",
  "failed",
]);

export const CreateAdHocPurchaseOrderResponseSchema = z.object({
  id: z.string().uuid(),
  status: z.enum([
    "pending_approval",
    "rejected",
    "pending",
    "submitted",
    "acked",
    "errored",
  ]),
  totalAmount: z.string(),
  lineCount: z.number().int().positive(),
  approvalDispatchStatus:
    AdHocPurchaseOrderApprovalDispatchStatusSchema.nullable(),
});

export type CreateAdHocPurchaseOrderResponse = z.infer<
  typeof CreateAdHocPurchaseOrderResponseSchema
>;

export const RetryAdHocPurchaseOrderApprovalDispatchInputSchema = z.object({
  draftPurchaseOrderId: z.string().uuid(),
});

export const RetryAdHocPurchaseOrderApprovalDispatchResponseSchema = z.object({
  draftPurchaseOrderId: z.string().uuid(),
  approvalDispatchStatus: AdHocPurchaseOrderApprovalDispatchStatusSchema,
});

export const CreateAdHocTransferOrderInputSchema = z
  .object({
    idempotencyKey: z.string().uuid(),
    worksheetDraftId: z.string().uuid().optional(),
    acknowledgeProtectedStock: z.boolean().optional(),
    sourceWarehouseId: z.string().uuid(),
    destinationWarehouseId: z.string().uuid(),
    lines: z
      .array(
        z.object({
          productV3Key: z.string().min(1).max(255),
          quantity: TransferQuantitySchema,
        }),
      )
      .min(1)
      .max(200),
  })
  .refine((input) => input.sourceWarehouseId !== input.destinationWarehouseId, {
    message: "Source and destination warehouses must differ",
    path: ["destinationWarehouseId"],
  });

export type CreateAdHocTransferOrderInput = z.infer<
  typeof CreateAdHocTransferOrderInputSchema
>;

export const CreateAdHocTransferOrderResponseSchema = z.object({
  id: z.string().uuid(),
  status: z.enum(["pending", "submitted", "acked", "errored"]),
  lineCount: z.number().int().positive(),
});

export type CreateAdHocTransferOrderResponse = z.infer<
  typeof CreateAdHocTransferOrderResponseSchema
>;

export const AdHocTransferWorksheetLineSchema = z.object({
  productV3Key: z.string().min(1).max(255),
  quantity: z.string().max(64),
});

export const SaveAdHocTransferWorksheetInputSchema = z
  .object({
    draftId: z.string().uuid(),
    idempotencyKey: z.string().uuid(),
    sourceWarehouseId: z.string().uuid().nullable(),
    destinationWarehouseId: z.string().uuid().nullable(),
    lines: z.array(AdHocTransferWorksheetLineSchema).max(200),
  })
  .refine(
    (input) =>
      input.sourceWarehouseId === null ||
      input.destinationWarehouseId === null ||
      input.sourceWarehouseId !== input.destinationWarehouseId,
    {
      message: "Source and destination warehouses must differ",
      path: ["destinationWarehouseId"],
    },
  );

export type SaveAdHocTransferWorksheetInput = z.infer<
  typeof SaveAdHocTransferWorksheetInputSchema
>;

export const AdHocTransferWorksheetProductSchema =
  AdHocTransferWorksheetLineSchema.extend({
    productSku: z.string().nullable(),
    productName: z.string().nullable(),
    availableQuantity: z.number(),
  });

export const AdHocTransferWorksheetSchema = z.object({
  draftId: z.string().uuid(),
  idempotencyKey: z.string().uuid(),
  sourceWarehouseId: z.string().uuid().nullable(),
  destinationWarehouseId: z.string().uuid().nullable(),
  lines: z.array(AdHocTransferWorksheetProductSchema),
  updatedAt: z.string(),
});

export type AdHocTransferWorksheet = z.infer<
  typeof AdHocTransferWorksheetSchema
>;

export const GetAdHocTransferWorksheetInputSchema = z.object({
  draftId: z.string().uuid(),
});

export const GetAdHocTransferPlanningInputSchema = z
  .object({
    sourceWarehouseId: z.string().uuid(),
    destinationWarehouseId: z.string().uuid(),
    productV3Keys: z.array(z.string().min(1).max(255)).min(1).max(200),
  })
  .refine((input) => input.sourceWarehouseId !== input.destinationWarehouseId, {
    message: "Source and destination warehouses must differ",
    path: ["destinationWarehouseId"],
  });

export const AdHocTransferPlanningPositionSchema = z.object({
  netPosition: z.number(),
  surplusPosition: z.number(),
  transferableSurplus: z.number().nonnegative(),
  physicalAvailable: z.number().nonnegative(),
  monthlyDemand: z.number().nonnegative().nullable(),
});

export const GetAdHocTransferPlanningResponseSchema = z.object({
  products: z.array(
    z.object({
      productV3Key: z.string(),
      source: AdHocTransferPlanningPositionSchema,
      destination: AdHocTransferPlanningPositionSchema,
    }),
  ),
});

export type GetAdHocTransferPlanningInput = z.infer<
  typeof GetAdHocTransferPlanningInputSchema
>;
export type GetAdHocTransferPlanningResponse = z.infer<
  typeof GetAdHocTransferPlanningResponseSchema
>;

// ─── Confirm carts → create POs + TOs ───────────────────────────────────────

export const ConfirmCartsInputSchema = z.object({
  // The vendor carts the buyer signed off on. Cart = (supplier, ship-to).
  carts: z
    .array(
      z.object({
        supplierId: z.string().uuid(),
        shipToWarehouseId: z.string().uuid(),
        // The freight and sending the buyer picked in the release dialog.
        // Left out, a new PO gets the suggested freight and the vendor's
        // sending defaults, and an open PO that lines are added to keeps its
        // own.
        freight: ReleaseFreightChoiceSchema.optional(),
        sending: DraftSubmissionChoiceSchema.optional(),
      }),
    )
    .min(1),
  divisionId: z.string().uuid().optional(),
  divisionIds: z.array(z.string().uuid()).optional(),
  // Suggestions the buyer locally toggled OUT of the cart this session. The
  // board's include-toggle is buyer-controlled and separate from the engine's
  // recommendedAction, so creation must honor these exclusions.
  excludeSuggestionIds: z.array(z.string().uuid()).optional(),
  // When set, ONLY these suggestions are released (still limited to committed
  // lines in the selected carts). The buying screen sends the in-cart lines.
  suggestionIds: z.array(z.string().uuid()).min(1).optional(),
  // Add the released buy lines to this unsent draft PO (status `pending` only)
  // instead of creating a new one, and re-run its approval. Single-cart only,
  // and requires `suggestionIds` (rejected otherwise).
  appendToDraftPurchaseOrderId: z.string().uuid().optional(),
  // When true, also create POs for carts below the vendor's hard MOV. When
  // false (default), below-MOV carts are skipped and returned in
  // `blockedBelowMov` so the UI can warn and re-confirm with acknowledgement.
  acknowledgeBelowMov: z.boolean().optional().default(false),
  // Required when `acknowledgeBelowMov` is true — audit trail for the override.
  belowMovOverrideComment: z.string().trim().min(1).optional(),
});

export type ConfirmCartsInput = z.infer<typeof ConfirmCartsInputSchema>;

export const ConfirmCartsResponseSchema = z.object({
  // Carts skipped because cartTotal < hard MOV and acknowledgeBelowMov=false.
  blockedBelowMov: z.array(
    z.object({
      supplierId: z.string().uuid(),
      supplierName: z.string().nullable(),
      shipToWarehouseId: z.string().uuid(),
      shipToWarehouseName: z.string().nullable(),
      cartTotal: z.number(),
      movThreshold: z.number(),
    }),
  ),
  // Model D: these are DRAFT orders (im_draft_purchase_orders / _transfer_orders).
  // `id` is the draft id; there is no AS400 number yet (assigned on ack), so no
  // externalId/transferNumber here — they no longer use a placeholder scheme.
  createdPurchaseOrders: z.array(
    z.object({
      id: z.string().uuid(),
      supplierId: z.string().uuid(),
      supplierName: z.string().nullable(),
      shipToWarehouseId: z.string().uuid().nullable(),
      lineCount: z.number(),
      totalAmount: z.number(),
    }),
  ),
  createdTransferOrders: z.array(
    z.object({
      id: z.string().uuid(),
      sourceWarehouseId: z.string().uuid().nullable(),
      destinationWarehouseId: z.string().uuid().nullable(),
      lineCount: z.number(),
    }),
  ),
  // Draft POs that received lines through `appendToDraftPurchaseOrderId`.
  appendedPurchaseOrders: z
    .array(
      z.object({
        id: z.string().uuid(),
        addedLineCount: z.number(),
        totalAmount: z.number(),
        status: z.enum(["pending_approval", "pending"]),
      }),
    )
    .optional(),
  suggestionsConfirmed: z.number(),
});

export type ConfirmCartsResponse = z.infer<typeof ConfirmCartsResponseSchema>;

// ─── Open (unsent) draft POs for a vendor × ship-to ─────────────────────────
// What Release offers to add to. `pending_approval` is mid-approval; `pending`
// is approved/released but not yet sent (nothing pushes to AS400 yet).

export const ListOpenDraftPurchaseOrdersInputSchema = z.object({
  supplierId: z.string().uuid(),
  shipToWarehouseId: z.string().uuid(),
});

export const ListOpenDraftPurchaseOrdersResponseSchema = z.object({
  orders: z.array(
    z.object({
      id: z.string().uuid(),
      trackingNo: z.string().nullable(),
      status: z.enum(["pending_approval", "pending"]),
      totalAmount: z.number(),
      createdAt: z.string(),
      /**
       * An approval gate already recorded a decision on this draft. Adding
       * lines would not re-ask that gate, so Release never adds to it.
       */
      hasApprovalDecision: z.boolean(),
    }),
  ),
});

export type ListOpenDraftPurchaseOrdersResponse = z.infer<
  typeof ListOpenDraftPurchaseOrdersResponseSchema
>;

// ─── Confirm transfer carts → create DRAFT TOs ──────────────────────────────
// The supplier-anchored confirmCarts can't mint these (no vendor). A transfer
// "cart" is (source → destination). Only its approved lines are released, the
// same rule as a vendor cart (lineApprovalState.ts).

export const ConfirmTransfersInputSchema = z.object({
  carts: z
    .array(
      z.object({
        sourceWarehouseId: z.string().uuid(),
        destinationWarehouseId: z.string().uuid(),
      }),
    )
    .min(1),
  divisionId: z.string().uuid().optional(),
  divisionIds: z.array(z.string().uuid()).optional(),
  warehouseIds: z.array(z.string().uuid()).optional(),
  // Suggestions the buyer locally toggled OUT of the cart this session.
  excludeSuggestionIds: z.array(z.string().uuid()).optional(),
  // Release only these lines (the open cart's approved lines in view), as
  // confirmCarts does; omitted = every approved line of the selected carts.
  suggestionIds: z.array(z.string().uuid()).optional(),
});

export type ConfirmTransfersInput = z.infer<typeof ConfirmTransfersInputSchema>;

export const ConfirmTransfersResponseSchema = z.object({
  // Model D: DRAFT transfer orders (im_draft_transfer_orders). `id` is the draft
  // id; AS400's transfer number is assigned later on ack.
  createdTransferOrders: z.array(
    z.object({
      id: z.string().uuid(),
      sourceWarehouseId: z.string().uuid().nullable(),
      destinationWarehouseId: z.string().uuid().nullable(),
      lineCount: z.number(),
    }),
  ),
  suggestionsConfirmed: z.number(),
});

export type ConfirmTransfersResponse = z.infer<
  typeof ConfirmTransfersResponseSchema
>;

// ─── Choose a pure transfer for one line → create its draft TO now ──────────
// GEN-2873: the buying screen is PO-only, so a transfer choice leaves the board
// immediately instead of waiting for a transfer Confirm.

export const ChooseTransferInputSchema = z.object({
  suggestionId: z.string().uuid(),
  sourceWarehouseId: z.string().uuid(),
  transferQuantity: TransferQuantitySchema,
});

export type ChooseTransferInput = z.infer<typeof ChooseTransferInputSchema>;

export const ChooseTransferResponseSchema = z.object({
  suggestionId: z.string().uuid(),
  // False when the line already had its transfer (a repeated call).
  created: z.boolean(),
  createdTransferOrders: z.array(
    z.object({
      id: z.string().uuid(),
      sourceWarehouseId: z.string().uuid(),
      destinationWarehouseId: z.string().uuid(),
      lineCount: z.number(),
    }),
  ),
});

export type ChooseTransferResponse = z.infer<
  typeof ChooseTransferResponseSchema
>;

// ─── Replenishment-created orders (procurement-module list view) ─────────────
//
// Per-type, server-side paginated/filterable/sortable list endpoints (one for
// POs, one for TOs). The orders page toggles between them. Mirrors the AR
// invoices list shape: filters + sortBy allowlist + limit/offset → { rows,
// pagination }.

export const ReplenishmentOrdersPaginationSchema = z.object({
  total: z.int().nonnegative(),
  limit: z.int().positive(),
  offset: z.int().nonnegative(),
});

const replenishmentOrdersBaseInput = {
  divisionId: z.string().uuid().optional(),
  divisionIds: z.array(z.string().uuid()).optional(),
  status: z.array(z.string()).optional(),
  search: z.string().optional(),
  sortDirection: z.enum(["asc", "desc"]).prefault("desc"),
  limit: z.int().min(1).max(200).prefault(50),
  offset: z.int().min(0).prefault(0),
};

export const ReplenishmentPurchaseOrderSortBySchema = z.enum([
  "trackingNo",
  "status",
  "supplierName",
  "shipToWarehouseName",
  "lineCount",
  "totalAmount",
  "createdByUserName",
  "orderDate",
  "createdAt",
]);

export const ReplenishmentTransferOrderSortBySchema = z.enum([
  "trackingNo",
  "status",
  "sourceWarehouseName",
  "destinationWarehouseName",
  "lineCount",
  "createdByUserName",
  "orderDate",
  "createdAt",
]);

export const ListReplenishmentPurchaseOrdersInputSchema = z.object({
  ...replenishmentOrdersBaseInput,
  myPendingApprovals: z.boolean().optional(),
  sortBy: ReplenishmentPurchaseOrderSortBySchema.prefault("createdAt"),
});
// Provenance of a draft transfer order: `replenishment` = a buyer confirmed it
// on the board; `replenishment_auto` = Phase 5 auto-staged a branch→hub transfer
// (opt-out). The transfers portal filters to `replenishment_auto` to show the
// "staged, going out unless cancelled" set.
export const ReplenishmentTransferOriginSystemSchema = z.enum([
  "replenishment",
  "replenishment_auto",
]);

export const ListReplenishmentTransferOrdersInputSchema = z.object({
  ...replenishmentOrdersBaseInput,
  ids: z.array(z.uuid()).max(50).optional(),
  originSystem: ReplenishmentTransferOriginSystemSchema.optional(),
  // Only orders staged at or after this instant, e.g. the viewer's midnight
  // for "auto-released today" (GEN-3026).
  createdSince: z.iso.datetime().optional(),
  sortBy: ReplenishmentTransferOrderSortBySchema.prefault("createdAt"),
});

export type ListReplenishmentPurchaseOrdersInput = z.infer<
  typeof ListReplenishmentPurchaseOrdersInputSchema
>;
export type ListReplenishmentTransferOrdersInput = z.infer<
  typeof ListReplenishmentTransferOrdersInputSchema
>;

// Model D unified order row: `kind` says whether `id` is a draft (pre-ack, in
// im_draft_*) or a real acked order (in the real PO/TO table, linked back via
// draft_*_order_id). `status` is the buyer-facing lifecycle:
//   pending_approval | pending | submitted | acked | errored.
// `pending_approval` is a draft PO held for role/$-authority approval (PO only).
// `trackingNo` shows once submitted; `externalId` is AS400's number (acked
// only); `outboundError` is set on `errored`.
export const ReplenishmentOrderKindSchema = z.enum(["draft", "real"]);
export const ReplenishmentOrderStatusSchema = z.enum([
  // Held for role/$-authority approval (created on Confirm, not yet released to
  // the outbound push). See docs/procurement/po-approval-workflow.md.
  "pending_approval",
  // An approval gate rejected the held draft (terminal, PO-only) — never picked
  // up for outbound.
  "rejected",
  "pending",
  "submitted",
  "acked",
  "errored",
]);
export const ReplenishmentTransferOrderStatusSchema = z.enum([
  ...ReplenishmentOrderStatusSchema.options,
  "draft",
]);

// Rollup of a held draft's approval bag (see docs/procurement/po-approval-workflow.md):
// `state` is the overall outcome; `decidedBy*`/`decidedAt`/`comment` are the latest
// terminal decision, for a "who & when" hover. Null when the PO never entered approval.
export const PurchaseOrderApprovalSummarySchema = z.object({
  state: z.enum(["awaiting", "approved", "rejected"]),
  decidedByName: z.string().nullable(),
  decidedAt: z.string().nullable(),
  comment: z.string().nullable(),
});

export const ReplenishmentPurchaseOrderRowSchema = z.object({
  id: z.string().uuid(),
  kind: ReplenishmentOrderKindSchema,
  status: ReplenishmentOrderStatusSchema,
  trackingNo: z.string().nullable(),
  externalId: z.string().nullable(),
  outboundError: z.string().nullable(),
  supplierId: z.string().uuid().nullable(),
  supplierName: z.string().nullable(),
  shipToWarehouseId: z.string().uuid().nullable(),
  shipToWarehouseName: z.string().nullable(),
  lineCount: z.number(),
  totalAmount: z.number().nullable(),
  createdByUserId: z.string().uuid().nullable(),
  createdByUserName: z.string().nullable(),
  orderDate: z.string().nullable(),
  createdAt: z.string(),
  approval: PurchaseOrderApprovalSummarySchema.nullable(),
});

export const ReplenishmentTransferOrderRowSchema = z.object({
  id: z.string().uuid(),
  kind: ReplenishmentOrderKindSchema,
  status: ReplenishmentTransferOrderStatusSchema,
  trackingNo: z.string().nullable(),
  externalId: z.string().nullable(),
  outboundError: z.string().nullable(),
  sourceWarehouseId: z.string().uuid().nullable(),
  sourceWarehouseName: z.string().nullable(),
  destinationWarehouseId: z.string().uuid().nullable(),
  destinationWarehouseName: z.string().nullable(),
  originSystem: ReplenishmentTransferOriginSystemSchema.nullable(),
  lineCount: z.number(),
  createdByUserId: z.string().uuid().nullable(),
  createdByUserName: z.string().nullable(),
  orderDate: z.string().nullable(),
  createdAt: z.string(),
});

export const ListReplenishmentPurchaseOrdersResponseSchema = z.object({
  rows: z.array(ReplenishmentPurchaseOrderRowSchema),
  pagination: ReplenishmentOrdersPaginationSchema,
});
export const ListReplenishmentTransferOrdersResponseSchema = z.object({
  rows: z.array(ReplenishmentTransferOrderRowSchema),
  pagination: ReplenishmentOrdersPaginationSchema,
});

export type ListReplenishmentPurchaseOrdersResponse = z.infer<
  typeof ListReplenishmentPurchaseOrdersResponseSchema
>;
export type ListReplenishmentTransferOrdersResponse = z.infer<
  typeof ListReplenishmentTransferOrdersResponseSchema
>;
export type ReplenishmentPurchaseOrderRow = z.infer<
  typeof ReplenishmentPurchaseOrderRowSchema
>;
export type ReplenishmentTransferOrderRow = z.infer<
  typeof ReplenishmentTransferOrderRowSchema
>;

// ─── PO approval progress (workflow-state pipeline) ──────────────────────────
// Tier-by-tier projection of a held draft PO's approval, mirroring the OMS-v3
// WorkflowPipeline: structure from the owning workflow run's definition,
// decisions from the im_draft_purchase_order_approvals store. See
// backend/src/core/services/procurement/draftPurchaseOrderWorkflowState.ts.

export const GetDraftPurchaseOrderWorkflowStateInputSchema = z.object({
  draftPurchaseOrderId: z.string().uuid(),
});

// Only approval gates a human was (or is) involved in are listed: awaiting
// gates plus human-decided ones. Non-approval steps, unreached gates, and
// auto-approved gates are omitted by the projection.
export const DraftPurchaseOrderWorkflowStateNodeSchema = z.object({
  id: z.string(),
  label: z.string(),
  sub: z.string(),
  role: z.string().nullable(),
  assignee: z.string().nullable(),
  // `pending` = raised, awaiting a decision.
  decision: z.enum([
    "approved",
    "rejected",
    "escalated",
    "timed_out",
    "pending",
  ]),
  // When the gate was decided (terminal) or raised (pending). ISO timestamp.
  decidedAt: z.string().nullable(),
  // The decider's free-text comment for this gate, or null.
  comment: z.string().nullable(),
  // Entered via an escalation edge (named escalation path / timed_out socket)
  // — the UI tags these "Escalation".
  enteredViaEscalation: z.boolean(),
});

export const DraftPurchaseOrderWorkflowStateSchema = z.object({
  hasWorkflow: z.boolean(),
  nodes: z.array(DraftPurchaseOrderWorkflowStateNodeSchema),
  // Counts cover only the listed gates (auto-approved ones are excluded).
  approvalsTotal: z.number(),
  approvalsDone: z.number(),
  rejected: z.boolean(),
  rejectionComment: z.string().nullable(),
  // Linked run + its workflow lineage id, for an admin "view run" deep link.
  workflowRunId: z.string().nullable(),
  workflowId: z.string().nullable(),
  // The raised-but-undecided gate's step run, backing the orders-board
  // "Decide in inbox" deep link (`/inbox?selId=approval-<stepRunId>`).
  // Null when no gate is currently awaiting a decision.
  pendingStepRunId: z.string().nullable(),
  // Whether the CALLER would see the pending gate in their inbox (approver
  // team member / configured user / OOO delegate / admin) — gates the CTA so
  // it never links someone to an inbox item they can't find. False when no
  // gate is pending.
  viewerCanDecide: z.boolean(),
});

export type GetDraftPurchaseOrderWorkflowStateInput = z.infer<
  typeof GetDraftPurchaseOrderWorkflowStateInputSchema
>;
export type DraftPurchaseOrderWorkflowState = z.infer<
  typeof DraftPurchaseOrderWorkflowStateSchema
>;

// ─── Draft PO detail (header + lines) ─────────────────────────────────────────
// One shared read model behind both PO detail surfaces — the inbox approval
// pane and the orders-board drill-in (docs/procurement/po-approval-workflow.md
// §7). Workflow approval progress is deliberately NOT here; compose with
// `getDraftPurchaseOrderWorkflowState`. The PO-local event-dispatch state is
// included so a failed ad hoc handoff remains recoverable after navigation.

export const GetDraftPurchaseOrderDetailInputSchema = z.object({
  draftPurchaseOrderId: z.string().uuid(),
});

export const DraftPurchaseOrderDetailLineSchema = z.object({
  id: z.string().uuid(),
  lineNumber: z.number().nullable(),
  productSku: z.string().nullable(),
  productName: z.string().nullable(),
  description: z.string().nullable(),
  quantity: z.number(),
  unitOfMeasure: z.string().nullable(),
  unitPrice: z.number().nullable(),
  totalPrice: z.number().nullable(),
  ppvReceiveAt: LineBuyTermsSchema.shape.ppvReceiveAt.default(null),
  costConstraintUnitCost: LineBuyTermsSchema.shape.costConstraintUnitCost,
});

export const DraftPurchaseOrderDetailSchema = z.object({
  id: z.string().uuid(),
  status: ReplenishmentOrderStatusSchema,
  trackingNo: z.string().nullable(),
  supplierId: z.string().uuid().nullable(),
  supplierName: z.string().nullable(),
  shipToWarehouseId: z.string().uuid().nullable(),
  shipToWarehouseName: z.string().nullable(),
  totalAmount: z.number().nullable(),
  orderDate: z.string().nullable(),
  createdByUserId: z.string().uuid().nullable(),
  createdByUserName: z.string().nullable(),
  createdAt: z.string(),
  approvalDispatchStatus:
    AdHocPurchaseOrderApprovalDispatchStatusSchema.nullable(),
  lines: z.array(DraftPurchaseOrderDetailLineSchema),
});

export type GetDraftPurchaseOrderDetailInput = z.infer<
  typeof GetDraftPurchaseOrderDetailInputSchema
>;
export type DraftPurchaseOrderDetailLine = z.infer<
  typeof DraftPurchaseOrderDetailLineSchema
>;
export type DraftPurchaseOrderDetail = z.infer<
  typeof DraftPurchaseOrderDetailSchema
>;

// ─── Retry / recall a pre-ack draft order ────────────────────────────────────
// `kind` selects the draft table; `id` is the draft order id.

export const ReplenishmentDraftKindSchema = z.enum(["po", "to"]);

export const RetryReplenishmentOrderInputSchema = z.object({
  kind: ReplenishmentDraftKindSchema,
  id: z.string().uuid(),
});
export const RetryReplenishmentOrderResponseSchema = z.object({
  id: z.string().uuid(),
  status: ReplenishmentOrderStatusSchema,
});
export type RetryReplenishmentOrderInput = z.infer<
  typeof RetryReplenishmentOrderInputSchema
>;
export type RetryReplenishmentOrderResponse = z.infer<
  typeof RetryReplenishmentOrderResponseSchema
>;

export const RecallReplenishmentOrderInputSchema = z.object({
  kind: ReplenishmentDraftKindSchema,
  id: z.string().uuid(),
});
export const RecallReplenishmentOrderResponseSchema = z.object({
  id: z.string().uuid(),
  // Suggestions returned to `pending` so they can be re-confirmed.
  suggestionsReverted: z.number(),
});
export type RecallReplenishmentOrderInput = z.infer<
  typeof RecallReplenishmentOrderInputSchema
>;
export type RecallReplenishmentOrderResponse = z.infer<
  typeof RecallReplenishmentOrderResponseSchema
>;

// Cancel an auto-staged transfer from the transfers portal. `block` also flags
// the destination branch's `auto_transfer_blocked` policy for the order's
// products, so the cancelled transfer is not silently re-staged next run.
export const CancelStagedTransferInputSchema = z.object({
  id: z.string().uuid(),
  block: z.boolean(),
});
export const CancelStagedTransferResponseSchema = z.object({
  id: z.string().uuid(),
  suggestionsReverted: z.number(),
  productsBlocked: z.number(),
});
export type CancelStagedTransferInput = z.infer<
  typeof CancelStagedTransferInputSchema
>;
export type CancelStagedTransferResponse = z.infer<
  typeof CancelStagedTransferResponseSchema
>;

// Line items of a single (draft) transfer order — for the transfers-portal
// row drill-in.
export const GetReplenishmentTransferOrderLinesInputSchema = z.object({
  id: z.string().uuid(),
});
export const ReplenishmentTransferOrderLineSchema = z.object({
  id: z.string().uuid(),
  /** `im_products_v3.key` (the SKU): what the line-detail drill-in keys on. */
  productV3Key: z.string().nullable(),
  productSku: z.string().nullable(),
  productName: z.string().nullable(),
  quantity: z.number(),
  unitOfMeasure: z.string().nullable(),
  /** Item weight in pounds per unit; null when none is on file. */
  unitWeightLbs: z.number().nullable(),
});
export const GetReplenishmentTransferOrderLinesResponseSchema = z.object({
  id: z.string().uuid(),
  sourceWarehouseName: z.string().nullable(),
  destinationWarehouseName: z.string().nullable(),
  lines: z.array(ReplenishmentTransferOrderLineSchema),
});
export type GetReplenishmentTransferOrderLinesInput = z.infer<
  typeof GetReplenishmentTransferOrderLinesInputSchema
>;
export type GetReplenishmentTransferOrderLinesResponse = z.infer<
  typeof GetReplenishmentTransferOrderLinesResponseSchema
>;
export type ReplenishmentTransferOrderLine = z.infer<
  typeof ReplenishmentTransferOrderLineSchema
>;

// ─── SO Status page (bulk lookup → header + line items + dependent POs/TOs) ──

export const GetSalesOrdersProcurementStatusInputSchema = z.object({
  // SO tokens: internal # or customer PO #. Bulk lookup (paste "1,2,3,4").
  queries: z.array(z.string()).max(50),
});

export const SalesOrderHeaderSchema = z.object({
  id: z.string().uuid(),
  internalId: z.number(),
  customerPurchaseOrderNumber: z.string().nullable(),
  status: z.string(),
  customerName: z.string().nullable(),
  orderDate: z.string(),
  deliveryDate: z.string().nullable(),
  totalAmount: z.number().nullable(),
});
export const SalesOrderLineItemRowSchema = z.object({
  id: z.string().uuid(),
  lineItemNumber: z.number().nullable(),
  productName: z.string().nullable(),
  productSku: z.string().nullable(),
  quantity: z.number().nullable(),
  unitPrice: z.number().nullable(),
  lineTotal: z.number().nullable(),
});
export const SalesOrderStatusResultSchema = z.object({
  query: z.string(),
  salesOrder: SalesOrderHeaderSchema.nullable(),
  lineItems: z.array(SalesOrderLineItemRowSchema),
  purchaseOrders: z.array(
    z.object({
      id: z.string().uuid(),
      externalId: z.string(),
      status: z.string(),
      supplierName: z.string().nullable(),
    }),
  ),
  transferOrders: z.array(
    z.object({
      id: z.string().uuid(),
      transferNumber: z.string(),
      status: z.string(),
      sourceWarehouseName: z.string().nullable(),
      destinationWarehouseName: z.string().nullable(),
    }),
  ),
});
export const GetSalesOrdersProcurementStatusResponseSchema = z.object({
  results: z.array(SalesOrderStatusResultSchema),
});

export type GetSalesOrdersProcurementStatusInput = z.infer<
  typeof GetSalesOrdersProcurementStatusInputSchema
>;
export type GetSalesOrdersProcurementStatusResponse = z.infer<
  typeof GetSalesOrdersProcurementStatusResponseSchema
>;
export type SalesOrderStatusResult = z.infer<
  typeof SalesOrderStatusResultSchema
>;

// Paginated SO Status table (mirrors the PO Status table).
export const ListSalesOrderStatusesInputSchema = z.object({
  internalIds: z.array(z.number().int()).optional(),
  status: z.array(z.string()).optional(),
  orderDateFrom: z.string().optional(),
  orderDateTo: z.string().optional(),
  sortBy: z
    .enum(["internalId", "status", "orderDate", "totalAmount", "customerName"])
    .prefault("orderDate"),
  sortDirection: z.enum(["asc", "desc"]).prefault("desc"),
  limit: z.int().min(1).max(200).prefault(25),
  offset: z.int().min(0).prefault(0),
});
export const SalesOrderStatusRowSchema = z.object({
  id: z.string().uuid(),
  internalId: z.number(),
  customerPurchaseOrderNumber: z.string().nullable(),
  customerName: z.string().nullable(),
  status: z.string(),
  orderDate: z.string(),
  totalAmount: z.number().nullable(),
  dependentPoCount: z.number(),
  dependentToCount: z.number(),
});
export const ListSalesOrderStatusesResponseSchema = z.object({
  rows: z.array(SalesOrderStatusRowSchema),
  pagination: ReplenishmentOrdersPaginationSchema,
});
export type ListSalesOrderStatusesInput = z.infer<
  typeof ListSalesOrderStatusesInputSchema
>;
export type ListSalesOrderStatusesResponse = z.infer<
  typeof ListSalesOrderStatusesResponseSchema
>;
export type SalesOrderStatusRow = z.infer<typeof SalesOrderStatusRowSchema>;

// ─── TO Status page (bulk lookup → TO + driving SOs + sibling POs) ───────────
//
// Inverse of the SO Status page: a transfer order *serves* the demand of the
// sales order(s) whose replenishment suggestions it fulfills. From those same
// driving SOs we also surface the "sibling" purchase orders (POs created for
// the same demand). The replenishment suggestion is the join:
//   TO  ← suggestion.transfer_order_id ; suggestion.trigger_sales_order_id → SO
//   SO  → suggestion.trigger_sales_order_id ; suggestion.purchase_order_id  → PO

export const GetTransferOrdersProcurementStatusInputSchema = z.object({
  // TO tokens: transfer # (alphanumeric) or internal # (numeric). Bulk lookup.
  queries: z.array(z.string()).max(50),
});

export const TransferOrderStatusHeaderSchema = z.object({
  id: z.string().uuid(),
  internalId: z.number(),
  transferNumber: z.string(),
  status: z.string(),
  sourceWarehouseName: z.string().nullable(),
  destinationWarehouseName: z.string().nullable(),
  orderDate: z.string().nullable(),
});
export const TransferOrderStatusResultSchema = z.object({
  query: z.string(),
  transferOrder: TransferOrderStatusHeaderSchema.nullable(),
  salesOrders: z.array(
    z.object({
      id: z.string().uuid(),
      internalId: z.number(),
      status: z.string(),
      customerName: z.string().nullable(),
    }),
  ),
  purchaseOrders: z.array(
    z.object({
      id: z.string().uuid(),
      externalId: z.string(),
      status: z.string(),
      supplierName: z.string().nullable(),
    }),
  ),
});
export const GetTransferOrdersProcurementStatusResponseSchema = z.object({
  results: z.array(TransferOrderStatusResultSchema),
});

export type GetTransferOrdersProcurementStatusInput = z.infer<
  typeof GetTransferOrdersProcurementStatusInputSchema
>;
export type GetTransferOrdersProcurementStatusResponse = z.infer<
  typeof GetTransferOrdersProcurementStatusResponseSchema
>;
export type TransferOrderStatusResult = z.infer<
  typeof TransferOrderStatusResultSchema
>;

// Paginated TO Status table (mirrors the SO Status table).
export const ListTransferOrderStatusesInputSchema = z.object({
  // Search tokens: transfer # (alphanumeric) or internal # (numeric).
  search: z.array(z.string()).optional(),
  status: z.array(z.string()).optional(),
  // Filter by where the TO came from, e.g. "replenishment" or "as400_gains".
  originSystems: z.array(z.string()).optional(),
  // Scope to TO destination warehouses in these divisions.
  divisionIds: z.array(z.string().uuid()).optional(),
  orderDateFrom: z.string().optional(),
  orderDateTo: z.string().optional(),
  sortBy: z
    .enum(["internalId", "transferNumber", "status", "orderDate"])
    .prefault("orderDate"),
  sortDirection: z.enum(["asc", "desc"]).prefault("desc"),
  limit: z.int().min(1).max(200).prefault(25),
  offset: z.int().min(0).prefault(0),
});
const TransferOrderStatusRowBaseSchema = z.object({
  id: z.string().uuid(),
  trackingNo: z.string().nullable(),
  status: z.string(),
  originSystem: z.string().nullable(),
  sourceWarehouseName: z.string().nullable(),
  destinationWarehouseName: z.string().nullable(),
  orderDate: z.string().nullable(),
  createdAt: z.string(),
  lineCount: z.number(),
  createdByUserName: z.string().nullable(),
  servesSoCount: z.number(),
  siblingPoCount: z.number(),
});
export const RealTransferOrderStatusRowSchema =
  TransferOrderStatusRowBaseSchema.extend({
    kind: z.literal("real"),
    internalId: z.number(),
    transferNumber: z.string(),
  });
export const TransferOrderStatusRowSchema = z.discriminatedUnion("kind", [
  RealTransferOrderStatusRowSchema,
  TransferOrderStatusRowBaseSchema.extend({
    kind: z.literal("draft"),
    internalId: z.null(),
    transferNumber: z.null(),
  }),
]);
export const ListTransferOrderStatusesResponseSchema = z.object({
  // Preserve the endpoint's original acknowledged-TO response for existing
  // clients. The updated status page consumes the additive merged view below.
  rows: z.array(RealTransferOrderStatusRowSchema),
  pagination: ReplenishmentOrdersPaginationSchema,
  mergedRows: z.array(TransferOrderStatusRowSchema),
  mergedPagination: ReplenishmentOrdersPaginationSchema,
});
export type ListTransferOrderStatusesInput = z.infer<
  typeof ListTransferOrderStatusesInputSchema
>;
export type ListTransferOrderStatusesResponse = z.infer<
  typeof ListTransferOrderStatusesResponseSchema
>;
export type TransferOrderStatusRow = z.infer<
  typeof TransferOrderStatusRowSchema
>;
export type RealTransferOrderStatusRow = z.infer<
  typeof RealTransferOrderStatusRowSchema
>;

// ─── Contract ───────────────────────────────────────────────────────────────

export const replenishmentSuggestionsContracts = {
  listSupplierSummaries: oc
    .input(ListSupplierSummariesInputSchema)
    .output(ListSupplierSummariesResponseSchema)
    .route({
      method: "GET",
      summary: "List suppliers with replenishment suggestions",
      description:
        "Returns one row per supplier with aggregated replenishment suggestion metrics such as line counts and order value, for the buying board.",
    }),
  getSupplierDetail: oc
    .input(GetSupplierDetailInputSchema)
    .output(GetSupplierDetailResponseSchema)
    .route({
      method: "GET",
      summary: "Get replenishment suggestions for a supplier",
      description:
        "Returns a supplier's replenishment suggestions with each line's top-ranked fulfillment option details.",
    }),
  getSuggestionDetail: oc
    .input(GetSuggestionDetailInputSchema)
    .output(GetSuggestionDetailResponseSchema)
    .route({
      method: "GET",
      summary: "Get a replenishment suggestion",
      description:
        "Returns a single replenishment suggestion with all of its ranked fulfillment options (buy, transfer, or combined).",
    }),
  listTransferSummaries: oc
    .input(ListTransferSummariesInputSchema)
    .output(ListTransferSummariesResponseSchema)
    .route({
      method: "GET",
      summary: "List transfer-only replenishment suggestions",
      description:
        "Returns replenishment suggestions fulfilled purely by warehouse transfer, not grouped by supplier.",
    }),
  listNoSourceSuggestions: oc
    .input(ListNoSourceSuggestionsInputSchema)
    .output(ListNoSourceSuggestionsResponseSchema)
    .route({
      method: "GET",
      summary: "List suggestions with no fulfillment source",
      description:
        "Returns replenishment suggestions that have no transfer source warehouse and no vendor mapping, so nothing can fill them.",
    }),
  listSupplierSummariesWithCarts: oc
    .input(ListSupplierSummariesInputSchema)
    .output(ListSupplierSummariesWithCartsResponseSchema)
    .route({
      method: "GET",
      summary: "List supplier carts on the buying board",
      description:
        "Returns supplier summaries with nested per-cart breakdowns, one cart per supplier and ship-to warehouse.",
    }),
  listBuyingOrders: oc
    .input(ListBuyingOrdersInputSchema)
    .output(ListBuyingOrdersResponseSchema)
    .route({
      method: "GET",
      summary: "List proposed buying orders",
      description:
        "Returns the buying screen's proposed orders, one row per purchase order or transfer order cart, aggregated, filtered, and paged.",
    }),
  buyingFilterOptions: oc
    .input(GetDemandFilterOptionsInputSchema)
    .output(GetDemandFilterOptionsResponseSchema)
    .route({
      method: "GET",
      summary:
        "List the shared procurement filter options for the buying board",
      description:
        "Returns the forecast grid's filter options (warehouse types, stock statuses, POP codes, ...) under the buying board's own permission, so its shared filters offer the same values as the forecast screens.",
    }),
  listBuyerCodes: oc
    .input(ListBuyerCodesInputSchema)
    .output(ListBuyerCodesResponseSchema)
    .route({
      method: "GET",
      summary: "List buyer codes",
      description:
        "Returns the distinct inventory-policy buyer codes in the selected divisions, used for the buying screen's buyer filter.",
    }),
  getBoardFreshness: oc
    .input(GetBoardFreshnessInputSchema)
    .output(GetBoardFreshnessResponseSchema)
    .route({
      method: "GET",
      summary: "Get when the buying board's data was last refreshed",
      description:
        "Per selected division, returns the latest replenishment run (start, end and outcome) and when the stock feed last updated the division's on-hand snapshot, so buyers can tell whether today's refresh finished.",
    }),
  getCartLines: oc
    .input(GetCartLinesInputSchema)
    .output(GetCartLinesResponseSchema)
    .route({
      method: "GET",
      summary: "Get suggestion lines in a cart",
      description:
        "Returns paginated replenishment suggestion lines for one cart, identified by supplier and ship-to warehouse.",
    }),
  getSupplierAssignments: oc
    .input(GetSupplierAssignmentsInputSchema)
    .output(GetSupplierAssignmentsResponseSchema)
    .route({
      method: "GET",
      summary: "Get cart assignments for a supplier",
      description:
        "Returns every suggestion line and cart assignment for one supplier, unpaginated, for reassigning lines between carts.",
    }),
  listVendorlessTransferSummaries: oc
    .input(ListVendorlessTransferSummariesInputSchema)
    .output(ListVendorlessTransferSummariesResponseSchema)
    .route({
      method: "GET",
      summary: "List transfer summaries by source warehouse",
      description:
        "Returns vendorless transfer suggestions grouped by source warehouse, as summary cards for the transfer board.",
    }),
  getSourceTransferAssignments: oc
    .input(GetSourceTransferAssignmentsInputSchema)
    .output(GetSourceTransferAssignmentsResponseSchema)
    .route({
      method: "GET",
      summary: "Get transfer lines for a source warehouse",
      description:
        "Returns all vendorless transfer lines and destination carts for one source warehouse, for assigning transfers.",
    }),
  applyOption: oc
    .input(ApplyOptionInputSchema)
    .output(ApplyOptionResponseSchema)
    .route({
      method: "POST",
      summary: "Choose a fulfillment option for a suggestion",
      description:
        "Sets which fulfillment option a replenishment suggestion will use, or clears the choice. Idempotent and does not change the suggestion's status.",
    }),
  applyPriceBreak: oc
    .input(ApplyPriceBreakInputSchema)
    .output(ApplyPriceBreakResponseSchema)
    .route({
      method: "POST",
      summary: "Raise buy quantity to next price break",
      description:
        "Bumps a buy line's quantity to the vendor's next price break, rounded to MOQ and order multiple, and saves it as the chosen buyer option. Fails if the offer changed.",
    }),
  setSuggestionApprovalState: oc
    .input(SetSuggestionApprovalStateInputSchema)
    .output(SetSuggestionApprovalStateResponseSchema)
    .route({
      method: "POST",
      summary: "Set the approval state of suggestions",
      description:
        "Marks pending replenishment suggestions approved, unapproved, or Trouble (a manual hold that keeps the line off any order). Only approved or auto-approved lines that are not in Trouble are released onto orders; setting any state ends auto-approval for the line. Returns the updated suggestion ids.",
    }),
  setLineBuyTerms: oc
    .input(SetLineBuyTermsInputSchema)
    .output(SetLineBuyTermsResponseSchema)
    .route({
      method: "POST",
      summary: "Set a buyer's terms on buy lines",
      description:
        "Saves a revised unit cost, contract number, PPV receive-at choice and PO line comment on pending replenishment suggestions. An omitted field keeps its value and null clears it. A revised cost reprices the line's current plan and is used on the draft purchase order. Returns the updated suggestion ids.",
    }),
  setRevisedUnitCosts: oc
    .input(SetRevisedUnitCostsInputSchema)
    .output(SetLineBuyTermsResponseSchema)
    .route({
      method: "POST",
      summary: "Set a revised unit cost per buy line",
      description:
        "Saves a revised unit cost on each listed pending replenishment suggestion in one transaction, for a percentage discount or markup, one set cost, or a reset (null) across several lines. Each revised cost reprices its line's current plan and is used on the draft purchase order. Returns the updated suggestion ids.",
    }),
  setSuggestionStatuses: oc
    .input(SetSuggestionStatusesInputSchema)
    .output(SetSuggestionStatusesResponseSchema)
    .route({
      method: "POST",
      summary: "Reject or restore many replenishment suggestions",
      description:
        "Moves up to 500 replenishment suggestions to rejected (trash) or back to pending in one transaction; no other status changes are allowed. Returns the ids that changed.",
    }),
  setSuggestionStatus: oc
    .input(SetSuggestionStatusInputSchema)
    .output(SetSuggestionStatusResponseSchema)
    .route({
      method: "POST",
      summary: "Reject or restore a replenishment suggestion",
      description:
        "Moves a replenishment suggestion to rejected (trash) or back to pending; no other status changes are allowed.",
    }),
  override: oc
    .input(OverrideOptionInputSchema)
    .output(OverrideOptionResponseSchema)
    .route({
      method: "POST",
      summary: "Override a suggestion's fulfillment",
      description:
        "Adds a buyer-authored fulfillment option with a custom action, quantity, vendor, or source warehouse, optionally making it the chosen option.",
    }),
  createOverflowLine: oc
    .input(CreateOverflowLineInputSchema)
    .output(CreateOverflowLineResponseSchema)
    .route({
      method: "POST",
      summary: "Add a stock line for a need's overflow",
      description:
        "Creates a buyer-added stock line at another line's item and location, planned as given. Engine runs keep it until it is ordered or trashed.",
    }),
  removeForwardBuy: oc
    .input(RemoveForwardBuyInputSchema)
    .output(RemoveForwardBuyResponseSchema)
    .route({
      method: "POST",
      summary: "Remove forward buying from a vendor cart",
      description:
        "Reverts every pending line in a supplier and ship-to cart whose chosen option is a forward-buy uplift to its baseline option. The next engine run may reapply it.",
    }),
  composePlan: oc
    .input(ComposePlanInputSchema)
    .output(ComposePlanResponseSchema)
    .route({
      method: "POST",
      summary: "Compose a multi-source fulfillment plan",
      description:
        "Saves a buyer plan that mixes transfers from several warehouses and a buy, such as transfer 5 from A plus buy 2, and by default makes it the chosen option.",
    }),
  getFulfillmentCandidates: oc
    .input(FulfillmentCandidatesInputSchema)
    .output(FulfillmentCandidatesResponseSchema)
    .route({
      method: "GET",
      summary: "List transfer sources and vendors for a product",
      description:
        "For a product and destination warehouse, returns warehouses with on-hand stock to transfer from and vendors to buy from, with price and minimum order value.",
    }),
  refresh: oc
    .input(
      z.object({
        // Business date (YYYY-MM-DD) the run prices against, e.g. to replay a
        // past batch. Omitted, the engine uses today's date in the
        // organization's timezone (um_organization_config.timezone, UTC when
        // unset).
        placementDate: z.iso.date().optional(),
      }),
    )
    .output(
      z.object({
        batchId: z.string().uuid(),
        // The placement date the run resolved and priced against.
        placementDate: z.iso.date(),
        needsDetected: z.number(),
        suggestionsCreated: z.number(),
        suggestionsUpdated: z.number(),
        suggestionsSkipped: z.number(),
        // Phase 4c forward buying. `cartsConsidered` counts every cart that
        // was under its MOV/freight target; `dollarsAdded` is net of any price
        // tier the uplift crossed.
        forwardBuyCartsConsidered: z.number(),
        forwardBuyCartsCleared: z.number(),
        forwardBuyLinesUplifted: z.number(),
        forwardBuyDollarsAdded: z.number(),
        durationMs: z.number(),
      }),
    )
    .route({
      method: "POST",
      summary: "Rerun the replenishment engine",
      description:
        "Recalculates replenishment needs and suggestions for the organization, including forward buying, optionally priced as of a placement date. Admin only; returns run counts.",
    }),
  getNeedInsights: oc
    .input(GetNeedInsightsInputSchema)
    .output(GetNeedInsightsResponseSchema)
    .route({
      method: "GET",
      summary: "Get decision-support data for a replenishment need",
      description:
        "Returns per-warehouse inventory, the destination's popularity code, and 12 months of destination sales history for one replenishment suggestion.",
    }),
  getLineStockPosition: getLineStockPositionContract,
  getLineSupplyDemand: getLineSupplyDemandContract,
  getProductInsights: oc
    .input(GetProductInsightsInputSchema)
    .output(GetProductInsightsResponseSchema)
    .route({
      method: "GET",
      summary: "Get buying insights for a product",
      description:
        "For a product, variant, and destination warehouse, returns per-warehouse inventory, popularity code, sales history, and forward forecast.",
    }),
  getProductSalesHistory: oc
    .input(GetProductSalesHistoryInputSchema)
    .output(GetProductSalesHistoryResponseSchema)
    .route({
      method: "GET",
      summary: "Get product sales history",
      description:
        "Returns monthly customer sales history for a product over a number of months, optionally for one warehouse, plus its forward forecast.",
    }),
  getProductMovementHistory: oc
    .input(GetProductMovementHistoryInputSchema)
    .output(GetProductMovementHistoryResponseSchema)
    .route({
      method: "GET",
      summary: "Get product inventory movement history",
      description:
        "Returns monthly outbound (sales, transfers out) and inbound (purchases, transfers in) quantities for a product, optionally for selected warehouses.",
    }),
  getProductOnHandHistory: oc
    .input(GetProductOnHandHistoryInputSchema)
    .output(GetProductOnHandHistoryResponseSchema)
    .route({
      method: "GET",
      summary: "Get product on-hand history",
      description:
        "Returns end-of-month historical on-hand quantity for a product, summed across an optional set of warehouses.",
    }),
  confirmCarts: oc
    .input(ConfirmCartsInputSchema)
    .output(ConfirmCartsResponseSchema)
    .route({
      method: "POST",
      summary: "Confirm carts into draft purchase orders",
      description:
        "Creates draft purchase orders and transfer orders from confirmed carts, or appends to an open draft, and accepts the suggestions. Starts approval when required; then sent to the ERP.",
    }),
  listOpenDraftPurchaseOrders: oc
    .input(ListOpenDraftPurchaseOrdersInputSchema)
    .output(ListOpenDraftPurchaseOrdersResponseSchema)
    .route({
      method: "GET",
      summary: "List open draft purchase orders for a supplier",
      description:
        "Returns unsent draft purchase orders, pending or awaiting approval, for a supplier and ship-to warehouse, newest first, so new lines can be added.",
    }),
  createAdHocPurchaseOrder: oc
    .input(CreateAdHocPurchaseOrderInputSchema)
    .output(CreateAdHocPurchaseOrderResponseSchema)
    .route({
      method: "POST",
      summary: "Create a manual purchase order",
      description:
        "Creates a buyer-entered draft supplier purchase order outside the suggestions and starts the division's approval workflow when one applies.",
    }),
  retryAdHocPurchaseOrderApprovalDispatch: oc
    .input(RetryAdHocPurchaseOrderApprovalDispatchInputSchema)
    .output(RetryAdHocPurchaseOrderApprovalDispatchResponseSchema)
    .route({
      method: "POST",
      summary: "Retry approval dispatch for a manual purchase order",
      description:
        "Re-sends or checks the approval workflow event for a buyer-entered draft purchase order whose approval dispatch did not complete.",
    }),
  createAdHocTransferOrder: oc
    .input(CreateAdHocTransferOrderInputSchema)
    .output(CreateAdHocTransferOrderResponseSchema)
    .route({
      method: "POST",
      summary: "Create a manual transfer order",
      description:
        "Creates a buyer-entered draft transfer order between two of the organization's warehouses.",
    }),
  saveAdHocTransferWorksheet: oc
    .input(SaveAdHocTransferWorksheetInputSchema)
    .output(AdHocTransferWorksheetSchema)
    .route({
      method: "POST",
      summary: "Save a transfer worksheet",
      description:
        "Creates or updates an in-progress manual transfer worksheet. Does not reserve inventory or create a transfer order.",
    }),
  getAdHocTransferWorksheet: oc
    .input(GetAdHocTransferWorksheetInputSchema)
    .output(AdHocTransferWorksheetSchema)
    .route({
      method: "GET",
      summary: "Get a transfer worksheet",
      description: "Loads an in-progress manual transfer worksheet by id.",
    }),
  getAdHocTransferPlanning: oc
    .input(GetAdHocTransferPlanningInputSchema)
    .output(GetAdHocTransferPlanningResponseSchema)
    .route({
      method: "GET",
      summary: "Get transfer planning figures",
      description:
        "For a manual transfer worksheet, returns each product's net position before transfer, transferable surplus at the source, and forecast demand.",
    }),
  chooseTransfer: oc
    .input(ChooseTransferInputSchema)
    .output(ChooseTransferResponseSchema)
    .route({
      method: "POST",
      summary: "Fulfill a suggestion by transfer",
      description:
        "Chooses a pure warehouse transfer for one pending replenishment line and immediately creates its draft transfer order, accepting the line. Idempotent.",
    }),
  confirmTransfers: oc
    .input(ConfirmTransfersInputSchema)
    .output(ConfirmTransfersResponseSchema)
    .route({
      method: "POST",
      summary: "Confirm transfer carts into transfer orders",
      description:
        "Creates draft transfer orders from confirmed vendorless transfer carts (source to destination warehouse) and marks their suggestions accepted. Idempotent.",
    }),
  listReplenishmentPurchaseOrders: oc
    .input(ListReplenishmentPurchaseOrdersInputSchema)
    .output(ListReplenishmentPurchaseOrdersResponseSchema)
    .route({
      method: "GET",
      summary: "List replenishment purchase orders",
      description:
        "Returns a filterable, sortable, paged list of replenishment draft purchase orders before acknowledgment, with status, tracking number, and send errors.",
    }),
  listReplenishmentTransferOrders: oc
    .input(ListReplenishmentTransferOrdersInputSchema)
    .output(ListReplenishmentTransferOrdersResponseSchema)
    .route({
      method: "GET",
      summary: "List replenishment transfer orders",
      description:
        "Returns a filterable, sortable, paged list of replenishment draft transfer orders before acknowledgment, with status, tracking number, and send errors.",
    }),
  getDraftPurchaseOrderWorkflowState: oc
    .input(GetDraftPurchaseOrderWorkflowStateInputSchema)
    .output(DraftPurchaseOrderWorkflowStateSchema)
    .route({
      method: "GET",
      summary: "Get approval progress for a draft purchase order",
      description:
        "Returns each approval tier for a held draft purchase order with its approver, decision, and who decided when.",
    }),
  getDraftPurchaseOrderDetail: oc
    .input(GetDraftPurchaseOrderDetailInputSchema)
    .output(DraftPurchaseOrderDetailSchema)
    .route({
      method: "GET",
      summary: "Get a draft purchase order",
      description:
        "Returns the header and line items of one replenishment draft purchase order, as shown in the approval pane and orders board.",
    }),
  retryReplenishmentOrder: oc
    .input(RetryReplenishmentOrderInputSchema)
    .output(RetryReplenishmentOrderResponseSchema)
    .route({
      method: "POST",
      summary: "Retry sending a failed replenishment order",
      description:
        "Moves an errored draft purchase or transfer order back to pending and clears its error, so it is sent to the ERP again.",
    }),
  recallReplenishmentOrder: oc
    .input(RecallReplenishmentOrderInputSchema)
    .output(RecallReplenishmentOrderResponseSchema)
    .route({
      method: "POST",
      summary: "Recall a replenishment order",
      description:
        "Deletes an unacknowledged draft purchase or transfer order and its lines and returns its suggestions to pending for reconfirmation. Refused once acknowledged.",
    }),
  cancelStagedTransfer: oc
    .input(CancelStagedTransferInputSchema)
    .output(CancelStagedTransferResponseSchema)
    .route({
      method: "POST",
      summary: "Cancel an auto-staged transfer",
      description:
        "Recalls an automatically staged branch transfer order, returning its suggestions to pending, and optionally blocks auto-transfers of those products to that branch.",
    }),
  getReplenishmentTransferOrderLines: oc
    .input(GetReplenishmentTransferOrderLinesInputSchema)
    .output(GetReplenishmentTransferOrderLinesResponseSchema)
    .route({
      method: "GET",
      summary: "Get replenishment transfer order lines",
      description:
        "Returns the products and quantities on one replenishment draft transfer order.",
    }),
  getSalesOrdersProcurementStatus: oc
    .input(GetSalesOrdersProcurementStatusInputSchema)
    .output(GetSalesOrdersProcurementStatusResponseSchema)
    .route({
      method: "GET",
      summary: "Look up procurement status for sales orders",
      description:
        "For each sales order number or customer PO number, returns the order, its lines, and the purchase and transfer orders it depends on, with live statuses.",
    }),
  listSalesOrderStatuses: oc
    .input(ListSalesOrderStatusesInputSchema)
    .output(ListSalesOrderStatusesResponseSchema)
    .route({
      method: "GET",
      summary: "List sales order procurement statuses",
      description:
        "Returns a filterable, sortable, paged list of sales orders with counts of the purchase and transfer orders each depends on.",
    }),
  getTransferOrdersProcurementStatus: oc
    .input(GetTransferOrdersProcurementStatusInputSchema)
    .output(GetTransferOrdersProcurementStatusResponseSchema)
    .route({
      method: "GET",
      summary: "Look up procurement status for transfer orders",
      description:
        "For each transfer order number, returns the sales orders it serves and sibling purchase orders from the same demand, with live statuses.",
    }),
  listTransferOrderStatuses: oc
    .input(ListTransferOrderStatusesInputSchema)
    .output(ListTransferOrderStatusesResponseSchema)
    .route({
      method: "GET",
      summary: "List transfer order procurement statuses",
      description:
        "Returns a filterable, sortable, paged list of transfer orders with counts of sales orders served and sibling purchase orders.",
    }),
  getForSalesOrder: oc
    .input(z.object({ salesOrderId: z.string().uuid() }))
    .output(
      z.object({
        items: z.array(
          z.object({
            triggerSalesOrderLineItemId: z.string().uuid(),
            suggestionId: z.string().uuid(),
            anchorSupplierId: z.string().uuid().nullable(),
            anchorSupplierName: z.string().nullable(),
            deliveryWarehouseId: z.string().uuid().nullable(),
            deliveryWarehouseName: z.string().nullable(),
            cartTotal: z.number().nullable(),
            lotLevelDeficit: z.boolean(),
            selectedUnitNumber: z.string().nullable(),
            unitQuantity: z.number().nullable(),
            fulfillment: FulfillmentSchema.nullable(),
          }),
        ),
      }),
    )
    .route({
      method: "GET",
      summary: "Get replenishment suggestions for a sales order",
      description:
        "For each line of a sales order, returns the pending replenishment suggestion it triggered, with supplier, delivery warehouse, cart total, and fulfillment choice.",
    }),
} as const;
